import { Fragment, useEffect, useRef, useState } from 'react'
import {
  api, can, ApiError, prettyPhone,
  type BookSave, type CallDraft, type ClosedCall, type Incident, type IncidentDetail, type ListItem, type Member,
  type Principal, type RadioSeat,
} from '../lib/api'
import { RadioBar } from './RadioBar'
import { CallerCard } from './CallerCard'
import { SiteNotes, Handover } from './SiteNotes'
import { PlaceMap } from './PlaceMap'
import { InboundTexts } from './InboundTexts'
import { FleetMap } from './FleetMap'
import type { PanelTx, PanelGroup } from './PanelHost'
import { Intake, PRIORITIES, type IntakeDraft, type IntakePrefill } from './Intake'
import { CombinedRebroadcast } from './CombinedRebroadcast'
import { BroadcastDrafts } from './BroadcastDrafts'
import { CallAnswers } from './CallAnswers'
import { CallNatures } from './CallNatures'
import { PhoneInput } from './PhoneInput'
import { MemberPicker } from './MemberPicker'
import { CallNotes } from './CallNotes'
import { CorrectCall } from './CorrectCall'
import { MARK_COLOURS } from '../lib/api'
import { readList, readValue, writeList } from '../lib/prefs'
import { AudiencePicker, reachesNobody, useAudiences } from './AudiencePicker'
import { BookChoice, CallbackRows, CallerRows, SendRows, ThreadRows, hhmm } from './CallParts'
import './dispatch.css'

/** The dispatcher screen, built against docs/design/dispatch-panel-v4.html.
 *
 *  Three columns — the queue, the call being worked, the map and roster — with
 *  the radio strip above them. The full dispatch panel is a second screen and
 *  opens in its own browser tab; this one is the CRM's own view of the same
 *  shift. */
export function Dispatch({
  me, seat, seats, talking, radioNote, transmit,
  groups, group, onGroup, panelElsewhere, onPopOut, newCallAsk = null, onNewCallTaken,
}: {
  me: Principal
  seat: RadioSeat | null
  seats: RadioSeat[]
  talking: PanelTx | null
  radioNote: string | null
  transmit: ((on: boolean) => void) | null
  groups: PanelGroup[]
  group: string | null
  onGroup: (id: string) => void
  panelElsewhere: boolean
  onPopOut: () => void
  /** The top bar's "+ New call" was pressed (9 Oct 2026): a fresh number
   *  per press, null once taken. */
  newCallAsk?: number | null
  onNewCallTaken?: () => void
}) {
  const [queue, setQueue] = useState<Incident[]>([])
  const [selected, setSelected] = useState<IncidentDetail | null>(null)
  /** WHICH CALL IS OPEN, READ AT THE MOMENT IT MATTERS rather than from the
   *  render a function was made in. `act` and the fifteen-second poll both
   *  outlive the render that created them, so reading `selected` there read
   *  the call as it was when the button was pressed -- which is how a call
   *  closed with "Confirm and close" came straight back onto the screen: the
   *  close cleared it, and the re-read that followed put back the call it
   *  remembered. Written only by `show`, so the two cannot disagree. */
  const selectedId = useRef<string | null>(null)
  function show(detail: IncidentDetail | null) {
    selectedId.current = detail?.id ?? null
    setSelected(detail)
  }
  const [members, setMembers] = useState<Member[]>([])
  /** WHICH TILES ARE LIT -- ANY MIX (the customer, 8 Oct 2026: "I should be
   *  able to do my own combination of those sections"). All is the three
   *  call tiles together and never the drafts; Drafts goes with anything;
   *  one is always lit. Remembered per browser. */
  const [tiles, setTiles] = useState<Set<TileKey>>(() => {
    const read = new Set(readList(TILES_PREF, TILE_KEYS, fromOldView()) as TileKey[])
    // "All" IS THE OTHER THREE, rebuilt from them rather than trusted from
    // storage: a stored "all" alone would light All over an empty list.
    if (read.has('all')) CALL_TILES.forEach((k) => read.add(k))
    else if (CALL_TILES.every((k) => k === 'all' || read.has(k))) read.add('all')
    return read
  })
  function toggleTile(key: TileKey) {
    // FROM THE CURRENT SET, not this render's: two quick clicks must both count.
    setTiles((tiles) => {
      const next = new Set(tiles)
      if (key === 'all') {
        if (next.has('all')) CALL_TILES.forEach((k) => next.delete(k))
        else CALL_TILES.forEach((k) => next.add(k))
      } else if (next.has(key)) {
        next.delete(key)
        if (CALL_TILES.includes(key)) next.delete('all')
      } else {
        next.add(key)
        if (CALL_TILES.every((k) => k === 'all' || next.has(k))) next.add('all')
      }
      // ONE ALWAYS LIT: an empty list with every tile dark reads as "nothing
      // open", which is the one thing this column must never say wrongly.
      if (next.size === 0) return tiles
      writeList(TILES_PREF, Array.from(next))
      return next
    })
  }
  const showAllCalls = () => setTiles((tiles) => {
    const next = new Set(tiles)
    CALL_TILES.forEach((k) => next.add(k))
    writeList(TILES_PREF, Array.from(next))
    return next
  })
  const [natures, setNatures] = useState<ListItem[]>([])
  const [relationships, setRelationships] = useState<ListItem[]>([])
  const [resolutions, setResolutions] = useState<ListItem[]>([])
  /** THE CALL BEING TAKEN, AS A DRAFT (069). The customer: "It should not be
   *  such a thing that when you start filling out a new call and somehow you
   *  jump to another that you lose it. It should always be saved as a draft."
   *  So every call taken is a draft on the server, the form is saved as it is
   *  typed, and clicking away -- to a call, another draft, a new one -- leaves
   *  it under Drafts exactly as it was. `id` is null until the server has made
   *  it: THE FORM NEVER WAITS FOR THAT, because a caller on the phone is not
   *  kept waiting for a list. */
  const [draft, setDraft] = useState<{ id: string | null; key: string; data: IntakeDraft } | null>(null)
  /** A draft another dispatcher has open, shown with Take over. */
  const [heldBy, setHeldBy] = useState<CallDraft | null>(null)
  /** Every open draft, every dispatcher's (his Q1 B). */
  const [drafts, setDrafts] = useState<CallDraft[]>([])
  /** CALLS CLOSED A MOMENT AGO (072): under the Closed tile, read-only, with
   *  Reopen -- the caller who said "it's fine" and then rang back. */
  const [closedCalls, setClosedCalls] = useState<ClosedCall[]>([])
  /** The closed call open in the middle, by id. */
  const [closedId, setClosedId] = useState<string | null>(null)
  /** Only a role that may reopen a call sees the Closed tile (072). */
  const mayReopen = can('calls.active.actions.close')
  async function loadClosed() {
    if (!mayReopen) return
    try {
      const list = await api.recentlyClosed()
      setClosedCalls(list)
      // PAST ITS WINDOW, OR REOPENED ON ANOTHER SCREEN: it leaves the middle
      // too, like a call that leaves the open queue.
      setClosedId((id) => (id && !list.some((c) => c.id === id) ? null : id))
    } catch {
      // The open calls are what matter; this comes back on the next poll.
    }
  }
  const [saved, setSaved] = useState<{ at: string | null; state: 'saved' | 'saving' | 'unsaved' }>(
    { at: null, state: 'saved' })
  const taking = draft !== null || heldBy !== null
  /** THE DRAFT IN THE MIDDLE, read by timers and requests that outlive the
   *  render they started in -- `selectedId`'s reason. */
  const cur = useRef<LiveDraft | null>(null)
  /** EVERY DRAFT THIS SCREEN STILL OWES THE SERVER SOMETHING: the one in the
   *  middle, and any it has left that are still being saved or let go. A
   *  save that fails after the dispatcher moved on is tried again from here
   *  rather than forgotten. */
  const lives = useRef<Set<LiveDraft>>(new Set())
  /** Bumped by every move to a call, a draft or a new call, so an answer that
   *  arrives after a later click does not put the earlier choice back. */
  const nav = useRef(0)
  const [error, setError] = useState<string | null>(null)
  /** The combined rebroadcast window (073), and what it last did. */
  const [combinedOpen, setCombinedOpen] = useState(false)
  const [combinedDone, setCombinedDone] = useState<string | null>(null)
  /** "Broadcast drafts" (074): several drafts opened, one first message. */
  const [draftsOpen, setDraftsOpen] = useState(false)
  const [now, setNow] = useState(() => new Date())
  const [ringing, setRinging] = useState<Awaited<ReturnType<typeof api.ringing>>['ringing']>([])
  // Which caller's card is open. At most one: two cards at once is two
  // conversations, and there is one dispatcher and one telephone.
  const [openCard, setOpenCard] = useState<string | null>(null)
  // Cards this dispatcher has closed or already acted on. Without it the
  // auto-open would put a dismissed card straight back two seconds later, on
  // the next poll, forever.
  const dismissed = useRef<Set<string>>(new Set())

  async function loadDrafts() {
    try {
      setDrafts(await api.callDrafts())
    } catch {
      // The calls are what matter; the list comes back on the next poll.
    }
  }

  const byKey = (key: string) => Array.from(lives.current).find((l) => l.key === key)

  /** Nothing more is written for it: it became a call, was discarded, or a
   *  colleague has it now. */
  function finish(live: LiveDraft) {
    live.done = true
    if (live.timer) { clearTimeout(live.timer); live.timer = null }
    lives.current.delete(live)
  }

  /** Put a draft from the server in the middle and start saving it. */
  function begin(d: CallDraft & { data: Record<string, unknown> }) {
    const data = d.data as unknown as IntakeDraft
    // A SHAPE THIS SCREEN DOES NOT KNOW IS NOT OPENED. Opened, the form would
    // save its own blank idea of it over somebody's call.
    if (!data || data.v !== 1) {
      api.callDraftRelease(d.id).catch(() => {})
      setError('This draft was saved by a newer version of this screen. Reload the page to open it.')
      loadDrafts()
      return
    }
    const json = stable(data)
    const live: LiveDraft = {
      key: d.id, id: d.id, version: d.version, newId: d.id, latest: data, latestJson: json,
      sentJson: json, timer: null, chain: Promise.resolve(), busy: false, saving: false,
      left: false, done: false, opened: false, ending: false, warned: false,
    }
    lives.current.add(live)
    cur.current = live
    setHeldBy(null)
    setSaved({ at: d.updated_at, state: 'saved' })
    setDraft({ id: d.id, key: live.key, data })
    loadDrafts()
  }

  /** NO LONGER OURS TO WRITE: a colleague took it over, opened it, or changed
   *  it. Said in his words, and the form leaves the middle -- what this screen
   *  still held is not written over theirs. */
  function lost(live: LiveDraft, e: unknown) {
    finish(live)
    if (cur.current === live) {
      cur.current = null
      setDraft(null)
    }
    setError(e instanceof ApiError ? e.message : 'This draft could not be saved.')
    loadDrafts()
  }

  /** THE ONE WRITER. Makes the draft if the server has not yet, saves the
   *  form if it changed since the last save SENT, and -- once the dispatcher
   *  has left it -- lets go, or discards it when nothing was ever typed. One
   *  at a time per draft, in order, each with the version before it. */
  function sync(live: LiveDraft): Promise<void> {
    if (live.timer) { clearTimeout(live.timer); live.timer = null }
    live.chain = live.chain.then(async () => {
      // NOT WHILE THE CALL IS OPENING: the draft becomes the call in that
      // same request, and a save racing it would be refused as "already
      // opened" and said as though something had gone wrong.
      if (live.done || live.busy) return
      const here = () => cur.current === live
      live.saving = true
      try {
        if (live.id === null) {
          // A "+ NEW CALL" LEFT BEFORE THE SERVER HAD IT, with nothing typed.
          if (live.left && blank(live.latest)) { finish(live); return }
          let made: Awaited<ReturnType<typeof api.callDraftCreate>>
          try {
            // THE SCREEN'S OWN ID, so a create whose answer was lost and is
            // sent again finds the first one rather than making a second.
            made = await api.callDraftCreate(live.latest as unknown as Record<string, unknown>, live.newId)
          } catch (e) {
            // NEVER TAKES THE FORM AWAY, whatever the refusal -- the drafts
            // list being full included. The call is still taken; it is just
            // not saved yet, and the heartbeat asks again.
            if (here()) {
              setSaved((s) => ({ ...s, state: 'unsaved' }))
              if (!live.warned && e instanceof ApiError && e.status !== 0) {
                live.warned = true
                setError(`This call is not being saved as a draft — ${e.message}`)
              }
            }
            return
          }
          live.id = made.id
          live.version = made.version
          // WHAT THE SERVER HOLDS, not what this request carried: a create
          // sent again gets back the FIRST one's form, and anything typed
          // since then is saved straight after.
          live.sentJson = stable(made.data)
          if (here()) setDraft((d) => (d && d.key === live.key ? { ...d, id: made.id } : d))
          if (here()) setSaved({ at: made.updated_at, state: 'saved' })
          loadDrafts()
        }
        const id = live.id
        // THE CALL WAS OPENED while the draft was still being made, so the
        // call does not know it: this copy goes, kept on record.
        if (live.opened) {
          await api.callDraftDiscard(id).catch(() => {})
          finish(live)
          loadDrafts()
          return
        }
        // A "+ NEW CALL" NOBODY TYPED INTO IS NOT A DRAFT. Left on the list
        // it would sit on every dispatcher's Drafts as "No nature yet" for
        // good. Discarded -- kept on record, like any discarded draft. One
        // from a ringing caller is never blank: it has the caller.
        if (live.left && blank(live.latest)) {
          live.ending = true
          await api.callDraftDiscard(id)
          finish(live)
          loadDrafts()
          return
        }
        if (live.latestJson !== live.sentJson) {
          const json = live.latestJson
          if (here()) setSaved((s) => ({ ...s, state: 'saving' }))
          const r = await api.callDraftSave(id, live.latest as unknown as Record<string, unknown>, live.version)
          live.version = r.version
          live.sentJson = json
          if (here()) {
            setSaved({ at: r.updated_at, state: live.latestJson === json ? 'saved' : 'saving' })
          }
          // TYPED (OR UNDONE) WHILE THAT WAS ON ITS WAY: it goes next --
          // after the usual pause while he is still typing, at once once he
          // has left -- unless a pause is already counting down.
          if (live.latestJson !== json && !live.timer) {
            live.timer = window.setTimeout(() => { sync(live) }, live.left ? 0 : 800)
          }
        }
        if (live.left && live.latestJson === live.sentJson) {
          live.ending = true
          await api.callDraftRelease(id)
          // REOPENED WHILE THAT WAS ON ITS WAY: it is the draft in the middle
          // again, so it goes on being saved -- the next save takes the hold
          // back.
          if (!live.left) {
            live.ending = false
            return
          }
          finish(live)
          loadDrafts()
        } else if (here() && live.latestJson === live.sentJson) {
          setSaved((s) => ({ ...s, state: 'saved' }))
        }
      } catch (e) {
        if (draftLost(e)) {
          if (!live.busy && !live.done) lost(live, e)
          return
        }
        // THE NETWORK, NOT A REFUSAL: kept, and the heartbeat tries again --
        // for a draft the dispatcher has left as much as for this one.
        if (here()) setSaved((s) => ({ ...s, state: 'unsaved' }))
      } finally {
        live.saving = false
      }
    })
    return live.chain
  }

  /** The form changed. Saved 0.8 s after the typing stops. */
  function snapshot(key: string, d: IntakeDraft) {
    const live = byKey(key)
    if (!live || live.done || live.left) return
    live.latest = d
    live.latestJson = stable(d)
    if (live.latestJson === live.sentJson) return
    if (live.timer) clearTimeout(live.timer)
    live.timer = window.setTimeout(() => { sync(live) }, 800)
  }

  /** The call is being opened from the form (or the caller joined). */
  function busy(key: string, on: boolean) {
    const live = byKey(key)
    if (!live) return
    live.busy = on
    // It did not open: back to saving, the hold kept or let go as before.
    if (!on && !live.done) sync(live)
  }

  /** LEAVE THE DRAFT IN THE MIDDLE, keeping everything: saved and then let go
   *  IN THE BACKGROUND, so moving to another call never waits on the network. */
  function leave() {
    const live = cur.current
    cur.current = null
    setDraft(null)
    setHeldBy(null)
    if (!live) return
    live.left = true
    sync(live)
  }

  /** "+ NEW CALL" -- always there, even in the middle of another call or
   *  draft (his words: "I should be able to open multiple new calls even in
   *  the middle of accepting another one"). The one in the middle is kept as
   *  a draft; this one is a draft of its own, made on the server behind the
   *  form rather than before it. */
  function newCall(prefill: IntakePrefill | null) {
    nav.current += 1
    setClosedId(null)
    leave()
    show(null)
    const data: IntakeDraft = { v: 1, prefill }
    const live: LiveDraft = {
      key: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`, id: null, version: 0,
      newId: uuid4(), latest: data, latestJson: stable(data), sentJson: '',
      timer: null, chain: Promise.resolve(), busy: false, saving: false, left: false,
      done: false, opened: false, ending: false, warned: false,
    }
    lives.current.add(live)
    cur.current = live
    setSaved({ at: null, state: 'saving' })
    setDraft({ id: null, key: live.key, data })
    sync(live)
  }

  // THE TOP BAR'S "+ New call", pressed on this screen or any other: the
  // Shell brings Dispatch up and this opens the form, exactly as the
  // column's own button does. Each press is its own number, taken once.
  const takenAsk = useRef<number | null>(null)
  useEffect(() => {
    if (newCallAsk == null || newCallAsk === takenAsk.current) return
    takenAsk.current = newCallAsk
    onNewCallTaken?.()
    newCall(null)
    // Forgotten on cleanup, so StrictMode's rehearsal unmount (development
    // only) -- which lets go of the blank draft just made -- is followed by
    // one fresh draft rather than a form that is saved nowhere.
    return () => { takenAsk.current = null }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newCallAsk])

  /** "BROADCAST DRAFTS" (074). The draft in the middle, if any, is saved and
   *  let go first -- waited for, so the window reads its last keys and does
   *  not find it held by this very screen. */
  async function openDraftsWindow() {
    setCombinedDone(null)
    const live = cur.current
    leave()
    if (live) await live.chain.catch(() => {})
    setDraftsOpen(true)
  }

  /** Back to a draft: held for this screen, or -- when a colleague has it
   *  open -- offered with Take over. */
  async function openDraft(c: CallDraft, force = false) {
    if (!force && cur.current?.id === c.id) return
    const mine = ++nav.current
    setClosedId(null)
    // ONE THIS SCREEN LEFT AND IS STILL SAVING comes back as this screen has
    // it -- the server's copy may not have the last keys yet.
    const settling = Array.from(lives.current).find((l) => l.id === c.id && !l.done && !l.ending)
    leave()
    show(null)
    if (settling && !force) {
      settling.left = false
      cur.current = settling
      setSaved({ at: null, state: settling.latestJson === settling.sentJson ? 'saved' : 'saving' })
      setDraft({ id: settling.id, key: settling.key, data: settling.latest })
      sync(settling)
      return
    }
    try {
      const d = await api.callDraftTake(c.id, force)
      if (nav.current !== mine) {
        api.callDraftRelease(d.id).catch(() => {})
        return
      }
      if (settling) finish(settling)
      begin(d)
    } catch (e) {
      if (nav.current !== mine) return
      if (e instanceof ApiError && e.status === 423) {
        setHeldBy(c)
        return
      }
      setError(e instanceof ApiError ? e.message : 'Could not open that draft.')
      loadDrafts()
    }
  }

  /** Off the list -- kept on record, never erased. */
  async function discardDraft() {
    const live = cur.current
    if (!live) return
    if (!blank(live.latest)
        && !window.confirm('Discard this draft? It leaves the Drafts list for everybody.')) return
    if (live.timer) { clearTimeout(live.timer); live.timer = null }
    live.busy = true
    await live.chain
    try {
      if (live.id) await api.callDraftDiscard(live.id)
      finish(live)
      if (cur.current === live) {
        cur.current = null
        setDraft(null)
      }
    } catch (e) {
      live.busy = false
      if (draftLost(e)) lost(live, e)
      else setError(e instanceof ApiError ? e.message : 'Could not discard the draft.')
    }
    loadDrafts()
  }

  // THE HEARTBEAT: the hold on the draft in the middle is renewed, and
  // anything a dropped connection kept back -- here or on a draft already
  // left -- is tried again.
  useEffect(() => {
    const beat = setInterval(() => {
      for (const live of Array.from(lives.current)) {
        if (live.done || live.busy || live.saving) continue
        if (live !== cur.current || live.id === null || live.latestJson !== live.sentJson) {
          sync(live)
          continue
        }
        const id = live.id
        live.chain = live.chain.then(() => api.callDraftKeep(id, live.version).then(
          () => {},
          (e) => { if (draftLost(e) && !live.busy) lost(live, e) },
        ))
      }
    }, 20_000)
    // CLOSING THE TAB: the last typing goes with `keepalive`, which the
    // browser finishes after the page is gone. Not while a save is already on
    // its way -- that one carries the same version and would be refused.
    function away() {
      for (const live of Array.from(lives.current)) {
        if (live.done || live.busy || live.saving || live.id === null) continue
        if (blank(live.latest)) {
          api.callDraftDiscard(live.id, true).catch(() => {})
        } else if (live.latestJson !== live.sentJson) {
          api.callDraftSave(live.id, live.latest as unknown as Record<string, unknown>,
            live.version, true).catch(() => {})
        } else {
          api.callDraftRelease(live.id, true).catch(() => {})
        }
      }
    }
    // BACK FROM THE BROWSER'S PAGE CACHE the versions held here are stale
    // (the keepalive save moved them on), so the page is read afresh.
    function back(e: PageTransitionEvent) {
      if (e.persisted) window.location.reload()
    }
    window.addEventListener('pagehide', away)
    window.addEventListener('pageshow', back)
    return () => {
      clearInterval(beat)
      window.removeEventListener('pagehide', away)
      window.removeEventListener('pageshow', back)
      // Leaving Dispatch for another screen of the CRM: saved and let go.
      const live = cur.current
      cur.current = null
      if (live) {
        live.left = true
        sync(live)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Open the intake form with NOTHING carried over.
   *
   *  `prefill` used to be cleared only when the form was cancelled, so after
   *  taking a call from the pop and finishing it, pressing "Start a call
   *  manually" reopened the form still holding the previous caller. The name
   *  and number were at least visible; the ADDRESS was not, and it dropped
   *  into the location box the moment a relationship was chosen -- the
   *  previous caller's house, on somebody else's incident, with a footnote
   *  explaining why it was right.
   *
   *  That is the wrong-place failure this whole design exists to prevent, so
   *  the prefill is cleared on every exit rather than on one of them. */
  function startBlank() {
    newCall(null)
  }

  // THE NATURE LIST IS READ AGAIN EVERY TIME A CALL IS TAKEN, not once when the
  // screen loads. It carries each nature's CODE, and the intake banner builds
  // the code the dispatcher reads out over the radio from it -- while the
  // question tree, and so the answer pieces, is fetched fresh for every form.
  // So a code set or changed in Settings after this screen opened joined
  // TODAY's pieces to a nature code hours old: he said one code on the air and
  // the queue card and call header, which the server freezes when the call
  // opens, showed another. That was most likely straight after the codes
  // arrived, with the owner typing them into live natures while dispatch
  // screens stayed open all shift.
  //
  // A failure keeps the list already held. An intake form with last hour's
  // natures is a call that can still be taken; an empty one is not.
  useEffect(() => {
    if (!draft) return
    api.settingsList('natures').then(setNatures).catch(() => {})
  }, [draft?.key])

  // Whether this customer has a radio at all. Distinct from "no seat right
  // now": one is a setup that does not include the panel, the other is a
  // dispatcher who cannot get on the air, and they read differently on screen.
  const radio = me.dispatch_state !== 'off'

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  async function loadQueue() {
    try {
      const list = await api.incidents(true)
      setQueue(list)
      // A call that has left the open list -- closed here or on another screen
      // -- leaves the middle too.
      const id = selectedId.current
      if (id && !list.some((c) => c.id === id)) show(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the queue.')
    }
  }

  /** A closed call, read-only in the middle, with Reopen (072). */
  function openClosed(id: string) {
    nav.current += 1
    leave()
    show(null)
    setClosedId(id)
  }

  async function open(id: string) {
    setClosedId(null)
    const mine = ++nav.current
    // A DRAFT IN THE MIDDLE IS KEPT, not dropped: saved, let go, and waiting
    // under Drafts.
    leave()
    try {
      const detail = await api.incident(id)
      // A later click -- "+ New call", another call -- wins.
      if (nav.current !== mine) return
      // CLOSED ON ANOTHER SCREEN SINCE THE QUEUE WAS READ (072): it is
      // readable inside the reopen window, and is shown as what it is --
      // closed, with Reopen -- never as a live call whose buttons all fail.
      if (detail.status === 'closed') {
        setClosedId(id)
        loadQueue()
        loadClosed()
        return
      }
      show(detail)
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not open that call.')
    }
  }

  useEffect(() => {
    loadQueue()
    loadDrafts()
    api.members().then(setMembers).catch(() => {})
    api.settingsList('natures').then(setNatures).catch(() => {})
    api.settingsList('relationships').then(setRelationships).catch(() => {})
    api.settingsList('resolutions').then(setResolutions).catch(() => {})
    loadClosed()
    const t = setInterval(() => { loadQueue(); loadDrafts(); loadClosed() }, 15_000)

    // A ring lasts seconds, so this polls faster than anything else on the
    // screen. Polling rather than a socket on purpose: two fields every two
    // seconds is not what will make this slow, and there is no reconnection
    // logic to get wrong at the moment the phone rings.
    const poll = setInterval(() => {
      api.ringing()
        .then((r) => {
          setRinging(r.ringing)

          // OPEN IT BY ITSELF, BUT ONLY WHEN HE IS ALONE.
          //
          // One dispatcher on duty: the call can only be for him, so putting
          // the caller in front of him is right and making him press a button
          // first is a step for nobody. Two or more watching: it must wait to
          // be asked, or it interrupts somebody mid-call — and worse, both of
          // them see it appear and each assumes the other has it.
          //
          // Only a LIVE call opens itself. One that has already ended lingers
          // on the strip and can be opened by hand, but nothing should jump
          // onto the screen for a call that is over.
          if (r.alone) {
            const fresh = r.ringing.find(
              (c) => c.live && !dismissed.current.has(c.linkedid),
            )
            if (fresh) setOpenCard((current) => current ?? fresh.linkedid)
          }

          // Forget calls that have gone entirely, so the same number ringing
          // back later opens again rather than being silently suppressed.
          const present = new Set(r.ringing.map((c) => c.linkedid))
          for (const id of Array.from(dismissed.current)) {
            if (!present.has(id)) dismissed.current.delete(id)
          }
        })
        .catch(() => setRinging([]))
    }, 2000)

    return () => { clearInterval(t); clearInterval(poll) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function act(fn: () => Promise<unknown>) {
    try {
      await fn()
      setError(null)
      await loadQueue()
      const id = selectedId.current
      if (id) {
        // A CALL CLOSED ON ANOTHER SCREEN IS NOT THIS ONE'S TO READ ANY MORE
        // (Past calls only), so a refusal here means "it is gone" and leaves the
        // middle quietly, like the queue poll does -- not a red banner about an
        // action that in fact worked.
        let fresh: Awaited<ReturnType<typeof api.incident>>
        try {
          fresh = await api.incident(id)
        } catch (e) {
          if (e instanceof ApiError && (e.status === 403 || e.status === 404)) {
            if (selectedId.current === id) show(null)
            return
          }
          throw e
        }
        // NOT PUT BACK if, while that was in flight, it was closed, collapsed,
        // or another call was opened in its place.
        if (selectedId.current === id) show(fresh.status === 'closed' ? null : fresh)
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not work.')
    }
  }

  const escalated = queue.filter((c) => c.escalation !== 'none').length
  const onDuty = members.filter((m) => m.on_duty && m.active)
  // The tints, once per render of the list, so the card and the open call's
  // header are computed from the same list and cannot disagree.
  const hues = tints(queue)
  const hueOf = (id: string) => hues[queue.findIndex((c) => c.id === id)] ?? 0
  // What the right column may hold for this role, and whether there is one.
  const showVehicles = can('map.vehicles')
  const showOnDuty = can('calls.side.on_duty')
  /** The texts strip (item 20, behind its own tick since 064), at the top
   *  of THIS column since 7 Oct 2026 -- his Q1 A: the left column is for
   *  calls only. Texts live on the Messaging tab, with the count on its
   *  button. */
  const showTexts = can('messaging.dispatch_strip')
  /** Equipment that is out (§07), at the bottom of this column since 7 Oct
   *  2026 -- the left one is for calls only. */
  const showOut = can('equipment.view')
  const side = showVehicles || showOnDuty || showTexts || showOut
  const responding = new Set(
    queue.flatMap((c) => (c.responder_count > 0 ? [c.id] : [])),
  )

  return (
    <div className="dispatch">
      {/* THE SECOND STRIP IS GONE (the customer, 6 Oct 2026: "remove this
          whole second strip"). It repeated the organization's name, the clock
          and the login, all on the top bar already, plus an on-duty count the
          right column lists and an equipment line that said nothing. The one
          thing on it that did a job -- popping the radio panel out -- stays,
          alone, and only while this customer has a radio panel. */}
      {radio && (
        <div className="topbar">
          <div className="spacer" />
          <button className="btn sm" onClick={onPopOut} disabled={!seat}>
            {panelElsewhere ? 'Panel is in another window' : 'Panel in its own window'}
          </button>
        </div>
      )}

      {/* The strip is the panel's mirror. With no panel there is nothing to
          mirror, and a permanently dead strip across the top of a dispatch
          screen trains people to ignore the one place that matters when it
          does come back. */}
      {radio && <RadioBar
        seat={seat}
        seats={seats}
        selected={selected}
        note={radioNote}
        talking={talking}
        groups={groups}
        group={group}
        onGroup={onGroup}
        transmit={transmit}
        panelElsewhere={panelElsewhere}
      />}

      {/* The caller-ID strip. One line per call, and the CARD is what opens —
          a ringing phone is not an incident. Most calls are a wrong number,
          somebody checking a time, or a caller ringing back about a job that
          already exists, and going straight to the intake form decided for the
          dispatcher that this one is work. */}
      {ringing.map((r) => (
        <div className={`ring ${r.live ? '' : 'over'}`} key={r.linkedid}>
          <span className={r.live ? 'pulse' : 'pulse still'} />
          <span className="lead">
            {r.live ? (r.answered_at ? 'On the phone' : 'Incoming') : 'Just ended'}
          </span>
          <div className="id">
            <b>{r.contact_name ?? r.caller_name ?? 'Unknown caller'}</b>
            <span>{r.caller_number ? prettyPhone(r.caller_number) : 'number withheld'}</span>
            {r.extension && <span>ext {r.extension}</span>}
            {!r.contact_id && <span className="pill">not in the phone book</span>}
          </div>
          <div className="acts">
            <button
              className={`btn sm ${openCard === r.linkedid ? '' : 'primary'}`}
              onClick={() =>
                setOpenCard(openCard === r.linkedid ? null : r.linkedid)
              }
            >
              {openCard === r.linkedid ? 'Close card' : 'Open caller'}
            </button>
          </div>
        </div>
      ))}

      {/* The card itself. It opens BY ITSELF when this dispatcher is the only
          one with a screen open — the call could only be for him, and making
          him press a button first is a step for nobody's benefit. With two or
          more watching it waits to be asked, because a card that appears on
          somebody's screen mid-call interrupts him, and both of them then
          assume the other has it. */}
      {ringing
        .filter((r) => r.linkedid === openCard)
        .map((r) => (
          <CallerCard
            key={r.linkedid}
            call={r}
            onClose={() => {
              setOpenCard(null)
              dismissed.current.add(r.linkedid)
            }}
            onAdded={loadQueue}
            onTake={(caller) => {
              setOpenCard(null)
              dismissed.current.add(r.linkedid)
              // The address travels with the caller. Dropping it here was why
              // the pop knew where somebody lived and the intake form did not.
              // A DRAFT LIKE ANY OTHER (069): a call taken from the pop is
              // kept as typed too, and keeps the caller it was taken for.
              newCall({
                caller_name: caller.caller_name,
                caller_phone: caller.caller_phone,
                caller_address: caller.caller_address,
                contact_id: caller.contact_id,
                pbx_name: caller.pbx_name ?? null,
              })
            }}
          />
        ))}

      {error && <div className="banner">{error}</div>}
      {combinedDone && (
        <div className="banner cb-done" role="status">
          {combinedDone}
          <button type="button" className="btn sm" onClick={() => setCombinedDone(null)}>OK</button>
        </div>
      )}
      {draftsOpen && (
        <BroadcastDrafts
          onClose={(note) => {
            setDraftsOpen(false)
            loadDrafts()
            loadQueue()
            if (note) setError(note)
          }}
          onDone={(said) => {
            setDraftsOpen(false)
            setCombinedDone(said)
            loadDrafts()
            loadQueue()
          }}
        />
      )}
      {combinedOpen && (
        <CombinedRebroadcast
          onClose={() => setCombinedOpen(false)}
          onSent={(n, members) => {
            setCombinedOpen(false)
            setCombinedDone(`Rebroadcast sent: ${n} call${n === 1 ? '' : 's'} in one message to ${members} member${members === 1 ? '' : 's'}.`)
            loadQueue()
            const id = selectedId.current
            if (id) api.incident(id).then((c) => { if (selectedId.current === id) show(c) }).catch(() => {})
          }}
        />
      )}

      {/* `nocall` is for a phone, where the queue and the call cannot sit
          side by side: with nothing open the list is the screen, and opening
          a call replaces it (the header's "← Open calls" comes back). */}
      <div className={`cols${side ? '' : ' noside'}${!selected && !taking && !closedId ? ' nocall' : ''}`}>
        {/* ---- the queue ------------------------------------------------ */}
        <div className="col queue">
          {/* NOTHING ABOVE THE CALLS (the customer, 7 Oct 2026: "I don't want
              any pop-ups or activities on top of that line ... I need this
              space in the room for all the calls"). The texts strip that sat
              here is at the top of the right column now. */}
          <div className="colhead dr-qhead">
            {/* "+ NEW CALL" ON A ROW OF ITS OWN, FULL WIDTH (the customer,
                9 Oct 2026, his Q1 A: "the button new calls should always be
                visible, never be covered in any situation"). It shared a row
                with the title, the count and Rebroadcast, which is wider than
                this 290 px column, and was pushed off its edge. Nothing sits
                beside it, so nothing added to this header can do that again.
                ALWAYS HERE, whatever is open in the middle (069). */}
            <button type="button" className="btn primary dr-newwide" onClick={() => newCall(null)}>
              + New call
            </button>
            <div className="dr-headrow">
              <h2>Open calls</h2>
              {/* Two numbers, because they answer different questions: how much
                  is open, and how much of it is going wrong. The second one used
                  to be "has no responder yet", which is true of every call in
                  its first ten seconds. */}
              <span className={`count ${escalated ? 'hot' : ''}`}>
                {escalated ? `${escalated} of ${queue.length}` : queue.length}
              </span>
              {/* ONE REBROADCAST FOR SEVERAL OPEN CALLS (073), for a role that
                  may broadcast, while anything is open. */}
              {can('calls.active.actions.broadcast') && queue.length > 0 && (
                <button type="button" className="btn sm cb-open"
                        onClick={() => { setCombinedDone(null); setCombinedOpen(true) }}>
                  ↻ Rebroadcast
                </button>
              )}
            </div>
          </div>
          {/* THE FIVE TILES, each with its count, any mix lit (8 Oct 2026).
              Drawn whenever anything is open or drafted; with neither there
              is nothing to choose between. */}
          {(queue.length > 0 || drafts.length > 0 || closedCalls.length > 0) && (
            <div className="views dr-views" role="group" aria-label="Which open calls to show">
              {VIEWS.map((v) => {
                const n = queue.filter((c) => v.fits(c.responder_count)).length
                return (
                  <button type="button" key={v.key} aria-pressed={tiles.has(v.key)}
                          className={`view ${'tone' in v ? v.tone : ''} ${tiles.has(v.key) ? 'on' : ''}`}
                          onClick={() => toggleTile(v.key)}>
                    {v.label} <b>{n}</b>
                  </button>
                )
              })}
              {/* THE FIFTH TILE (069): calls being taken, not yet open.
                  Dashed purple like the drafts themselves. */}
              <button type="button" aria-pressed={tiles.has('drafts')}
                      className={`view dr-view ${tiles.has('drafts') ? 'on' : ''}`}
                      onClick={() => toggleTile('drafts')}>
                Drafts <b>{drafts.length}</b>
              </button>
              {/* THE SIXTH TILE (072): calls closed a moment ago, grey -- for
                  a role that may reopen one. */}
              {mayReopen && (
                <button type="button" aria-pressed={tiles.has('closed')}
                        className={`view ro-view ${tiles.has('closed') ? 'on' : ''}`}
                        onClick={() => toggleTile('closed')}>
                  Closed <b>{closedCalls.length}</b>
                </button>
              )}
            </div>
          )}
          {(() => {
            // WHAT THE LIT TILES SHOW, together. The hue is by position in
            // the WHOLE queue, so a call keeps its tint whichever tiles are on.
            const shownDrafts = tiles.has('drafts') ? drafts : []
            const rows = queue.map((c, i) => [c, i] as const).filter(([c]) => {
              const n = c.responder_count
              return (n === 0 && tiles.has('none')) || (n === 1 && tiles.has('one'))
                || (n >= 2 && tiles.has('two'))
            })
            const shownClosed = tiles.has('closed') ? closedCalls : []
            const anything = shownDrafts.length > 0 || rows.length > 0 || shownClosed.length > 0
            return (
              <div className={`scroll ${anything || queue.length ? 'qlist' : ''}`}>
                {queue.length === 0 && shownDrafts.length === 0 && shownClosed.length === 0 ? (
                  <div className="empty">
                    <div className="big">Nothing open</div>
                    <p>Calls appear here the moment they are taken.<br />Longest waiting at the top.</p>
                    <button className="btn" onClick={startBlank}>
                      Start a call manually
                    </button>
                  </div>
                ) : !anything ? (
                  <div className="empty small">
                    <p>Nothing in the tiles you chose.</p>
                    <button className="btn sm" onClick={showAllCalls}>Show all calls</button>
                  </div>
                ) : (
                  <>
                    {/* DRAFTS ON TOP when they show with calls (his Q1 B). */}
                    {shownDrafts.map((d) => (
                      <DraftCard key={d.id} draft={d} now={now}
                                 open={draft?.id === d.id || heldBy?.id === d.id}
                                 onPick={() => openDraft(d)} />
                    ))}
                    {shownDrafts.length > 0 && rows.length > 0 && (
                      <div className="dr-sep">Open calls</div>
                    )}
                    {rows.map(([c, i]) => <QueueCall key={c.id} call={c} now={now}
                                                      selected={selected?.id === c.id}
                                                      onPick={() => open(c.id)}
                                                      onCollapse={() => show(null)}
                                                      hue={hues[i]} />)}
                    {/* UNDER THE OPEN ONES, grey: closed, and how long they
                        can still be reopened. */}
                    {shownClosed.length > 0 && (shownDrafts.length > 0 || rows.length > 0) && (
                      <div className="dr-sep">Closed — can be reopened</div>
                    )}
                    {shownClosed.map((c) => (
                      <button key={c.id} type="button"
                              className="qcall ro-card" aria-selected={closedId === c.id}
                              onClick={() => openClosed(c.id)}>
                        <div className="t">{age(c.closed_at, now)}<small>Closed</small></div>
                        <div>
                          <div className="n">
                            {c.call_code && <span className="callcode">{c.call_code}</span>}
                            {c.call_code && ' '}{c.nature_label}
                          </div>
                          <div className="a">{c.location ?? '—'}</div>
                          <div className="flags">
                            <span className="pill">
                              {c.resolution_label ?? 'Closed'}{c.closed_by_label ? ` · ${c.closed_by_label}` : ''}
                            </span>
                            <span className="pill ro-pill">Reopen until {hhmm(c.reopen_until)}</span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )
          })()}
          {/* "OUT NOW" LEFT THIS COLUMN (the customer, 7 Oct 2026: "On the
              left column, I only need all open calls"). It is at the bottom
              of the right column, with the real count. */}
          {/* BROADCAST DRAFTS (074), PINNED TO THE BOTTOM so the list never
              pushes it away: several drafts opened as calls, ONE message.
              Whenever a draft is waiting, for a role that may broadcast. */}
          {can('calls.active.actions.broadcast') && drafts.length > 0 && (
            <div className="bd-foot">
              <button type="button" className="btn bd-open" onClick={openDraftsWindow}>
                Broadcast drafts ({drafts.length})
              </button>
            </div>
          )}
        </div>

        {/* ---- the call being worked ------------------------------------ */}
        <div className="col work">
          {heldBy ? (
            <div className="wbody">
              {/* SOMEBODY ELSE IS FILLING IT (his Q1 B: a colleague can
                  finish one). Not drawn as a form that cannot be typed in --
                  said, with the one thing to do about it. */}
              <div className="card dr-held">
                <h2>Being filled by {heldBy.held_by_label ?? 'another dispatcher'}</h2>
                <p className="dr-what">
                  <b>{heldBy.nature_label ?? 'No nature yet'}</b>
                  {' · '}{heldBy.location_label ?? 'no address yet'}
                  {heldBy.caller_label ? ` · caller ${heldBy.caller_label}` : ''}
                </p>
                <p className="dr-note">
                  Take over and the draft moves to you, exactly as they left it.
                  Their screen says so and stops saving.
                </p>
                <div className="btnrow">
                  <button className="btn primary" onClick={() => openDraft(heldBy, true)}>Take over</button>
                  <button className="btn" onClick={() => setHeldBy(null)}>Leave it with them</button>
                </div>
              </div>
            </div>
          ) : draft ? (
            <div className="wbody intakebody">
              <Intake
                /* Remounted per draft. Without a key, React reuses the form
                   and its useState initialisers -- which is where the draft
                   is read -- never run again. */
                key={draft.key}
                natures={natures}
                relationships={relationships}
                roster={members}
                resolutions={resolutions}
                draft={draft.data}
                draftId={draft.id}
                saved={saved.state === 'saving' ? 'Saving…'
                  : saved.state === 'unsaved'
                    ? <span className="dr-warn">Not saved yet — trying again</span>
                    : `✓ Saved as a draft${saved.at ? ` · ${hhmm(saved.at)}` : ''}`}
                onSnapshot={(d) => snapshot(draft.key, d)}
                /* SAVE AS DRAFT (074): saved and let go, the form closed. */
                onSaveDraft={() => {
                  // NEVER CLOSED OVER A FORM THE SERVER DOES NOT HAVE: it
                  // would live only in this tab until a save got through.
                  if (saved.state === 'unsaved') {
                    setError('This call is not saved yet, so the form stays open. Try again in a moment.')
                    return
                  }
                  leave()
                  loadDrafts()
                }}
                onBusy={(b) => busy(draft.key, b)}
                /* THE WARNING IS SET LAST, AFTER THE CALL IS ON SCREEN.
                   `open()` clears the banner on success, so anything said
                   before it would be wiped by the hand-off itself — and what
                   this carries is the one thing the dispatcher cannot see for
                   himself: an answer he gave that the server did not store. */
                onDone={async (id, warning, logged) => {
                  // THE DRAFT IS THE CALL NOW -- marked by the server in the
                  // same request -- so nothing more is saved or let go.
                  //
                  // BY ITS OWN KEY, NOT "WHATEVER IS IN THE MIDDLE": the
                  // broadcast after the call opens takes a moment, and a
                  // dispatcher who pressed "+ New call" meanwhile is typing
                  // the next caller. That form is not touched, and the screen
                  // does not jump to the call he finished.
                  const key = draft.key
                  const here = cur.current?.key === key
                  const live = byKey(key)
                  if (live) {
                    live.opened = true
                    finish(live)
                    // THE CALL MAY HAVE GONE WITHOUT IT -- pressed in the
                    // moment between the draft being made and the form
                    // hearing its id. Left open it would be a second copy of
                    // the call on everybody's list. When the call did carry
                    // it, this is refused (already a call) and changes nothing.
                    if (live.id) api.callDraftDiscard(live.id).catch(() => {})
                  }
                  const step = nav.current
                  loadDrafts()
                  // A CALL THAT ALREADY HAPPENED, SAVED CLOSED (078): it is
                  // under Past calls, not on the board -- said, not opened.
                  if (logged?.closed) {
                    if (here) { cur.current = null; setDraft(null) }
                    setCombinedDone(`Saved ${logged.number} — logged afterwards and closed. `
                      + 'Nothing was sent. It is under Reports / History, Past calls.')
                    if (warning) setError(warning)
                    return
                  }
                  if (!here) {
                    await loadQueue()
                    if (warning) setError(warning)
                    return
                  }
                  cur.current = null
                  setDraft(null)
                  await loadQueue()
                  // "+ New call" pressed meanwhile wins.
                  if (nav.current === step) await open(id)
                  if (warning) setError(warning)
                }}
                onCancel={discardDraft}
                onError={setError}
              />
            </div>
          ) : closedId ? (
            <ClosedCallView
              key={closedId}
              id={closedId}
              until={closedCalls.find((c) => c.id === closedId)?.reopen_until ?? null}
              onReopened={async (id) => {
                const step = nav.current
                setClosedId(null)
                await Promise.all([loadQueue(), loadClosed()])
                // "+ New call" or another call pressed meanwhile wins.
                if (nav.current === step) await open(id)
              }}
              onGone={() => { setClosedId(null); loadClosed() }}
            />
          ) : selected ? (
            <WorkArea
              call={selected}
              hue={hueOf(selected.id)}
              members={members}
              resolutions={resolutions}
              natures={natures}
              seat={seat}
              act={act}
              onClosed={() => { show(null); loadQueue(); loadClosed() }}
              onCollapse={() => show(null)}
            />
          ) : (
            <div className="work-empty">
              <div className="mark">Standing by</div>
              <h3>No call selected</h3>
              <p style={{ maxWidth: 340, fontSize: 13.5, lineHeight: 1.7 }}>
                When the phone rings the caller record opens here.<br />
                Until then the queue stays live.
              </p>
              <button className="btn primary" onClick={startBlank}>
                Start a call manually
              </button>
            </div>
          )}
        </div>

        {/* ---- map and roster ------------------------------------------- */}
        {/* THE WHOLE COLUMN IS A PERMISSION MATTER. The customer, 17 Sep
            2026, on the dispatcher's login: "this whole right column should
            not be there." Vehicles answers to `map.vehicles`, the on-duty
            list to `calls.side.on_duty`, the texts strip to
            `messaging.dispatch_strip` and Out now to `equipment.view` (both
            here since 7 Oct 2026), and a role holding none of the four gets
            no column at all -- `noside` above
            lets the working call take the width -- rather than an empty panel
            with a border. A role with the texts tick alone gets a column
            that is mostly the strip; that tick is off on every seeded role. */}
        {side && (
        <div className="col side">
          {showTexts && <InboundTexts />}
          {/* THE HEADING GOES WITH THE PANE, or it is a label over nothing.
              FleetMap already returns null for a role without `map.vehicles`,
              so the browser never even asks for `/api/fleet` -- but the heading
              and the pane holding it were drawn regardless, and above 1180px
              that left a denied role reading the word "Vehicles" over most of
              an empty column with nothing anywhere saying why. Nobody sent to
              investigate could find a 403 either, because no request was ever
              made. FleetMap keeps its own gate: two gates agreeing is the
              point, and this one exists so the space is not claimed at all. */}
          {showVehicles && (
            <>
              <div className="colhead"><h2>Vehicles</h2></div>
              {/* NOT inside `.map`. That container is a centring flex ROW built
                  to hold one `.pending` block, so two children -- the canvas and
                  the vehicle list -- would sit side by side in a 336px column,
                  each squeezed to half of it. FleetMap brings its own layout. */}
              <div className="fleetpane">
                {/* Vehicles, live, from Traccar. The address on each open call
                    plots separately on the call card, under the site notes.
                    Volunteer positions still come from the panel and are not
                    here yet. */}
                <FleetMap />
              </div>
            </>
          )}
          {showOnDuty && (
          <div className="roster">
            <div className="colhead">
              <h2>On duty</h2><span className="count">{onDuty.length}</span>
            </div>
            {onDuty.length === 0 ? (
              <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--ink-faint)' }}>
                Nobody registered on duty. A broadcast would reach no one.
              </div>
            ) : (
              onDuty.map((m) => (
                <div className="rrow" key={m.id}>
                  <span className={`s ${responding.has(m.id) ? 'busy' : 'on'}`} />
                  <span className="cs">{m.callsign ?? '—'}</span>
                  {/* `label`, never `display_name`. That column is nullable
                      since migration 043 -- the unit number is the identity
                      and half a real roster has nobody filled in yet -- so
                      this rendered a bare dot and a callsign with an empty
                      space where a person should be. `label` is the name, or
                      the unit when there is no name, decided on the server so
                      no screen invents its own fallback. */}
                  {m.display_name ? m.label : ''}
                  {/* THE WORD, not just the dot. This said "Available" for
                      everybody, including members already on their way to a
                      call -- the only thing that changed was a 7px dot going
                      green to amber, and those two are within 1.02:1 of each
                      other in the light palette, which is also the classic
                      red-green confusion pair. A dispatcher scanning for
                      somebody free would read a committed responder as free,
                      in writing. */}
                  <span className="st">
                    {responding.has(m.id) ? 'Responding' : 'Available'}
                  </span>
                </div>
              ))
            )}
          </div>
          )}
          {showOut && <OutNowPanel />}
        </div>
        )}
      </div>
    </div>
  )
}

/** EQUIPMENT THAT IS OUT -- a pump on a driveway, a booster rented to a
 *  member of the public (§07). The bottom of the right column: quiet, never
 *  opens anything, and the full list is the Equipment tab. Read every minute;
 *  nothing here is time-critical. */
function OutNowPanel() {
  const [out, setOut] = useState<Awaited<ReturnType<typeof api.outNow>> | null>(null)
  useEffect(() => {
    let alive = true
    const load = () => api.outNow().then((o) => { if (alive) setOut(o) }).catch(() => {})
    load()
    const t = setInterval(load, 60_000)
    return () => { alive = false; clearInterval(t) }
  }, [])
  const loans = out?.loans ?? []
  return (
    <div className="ot-panel">
      <div className="colhead">
        <h2>Out now</h2>
        <span className={`count ${out?.due ? 'hot' : ''}`}>{out ? loans.length : '–'}</span>
      </div>
      {/* NOT "Nothing out" before the server has said so: a dead connection
          must not read as an empty driveway. The last good answer stays. */}
      {!out ? (
        <div className="ot-none">Not loaded yet.</div>
      ) : loans.length === 0 ? (
        <div className="ot-none">Nothing out.</div>
      ) : (
        <>
          {loans.slice(0, 5).map((l) => (
            <div className="rrow" key={l.id}>
              <span className="cs">{l.asset_tag}</span>
              <span className="ot-what">{l.type_name}</span>
              <span className="st">
                {l.due ? <span className="pill wait">Due back</span> : `${l.days_out}d`}
              </span>
            </div>
          ))}
          {loans.length > 5 && (
            <div className="ot-none">and {loans.length - 5} more — see the Equipment tab.</div>
          )}
        </>
      )}
    </div>
  )
}

function elapsed(from: string, now: Date): string {
  const secs = Math.max(0, Math.floor((now.getTime() - new Date(from).getTime()) / 1000))
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/** What each escalation state says, and how loud it is.
 *
 *  Worded as the thing that is wrong rather than the name of a timer: a
 *  dispatcher reading "Nobody has taken it" knows what to do, and one reading
 *  "unanswered_after_seconds exceeded" does not. */
const ESCALATION: Record<string, { label: string; heat: string }> = {
  not_broadcast: { label: 'Never sent — nobody has been asked', heat: 'crit' },
  relocation: { label: 'Moved, and the responder has not confirmed', heat: 'crit' },
  unanswered: { label: 'Nobody has taken it', heat: 'crit' },
  stalled: { label: 'Taken, then nothing', heat: 'warn' },
}

/** THE FOUR VIEWS OVER THE OPEN CALLS (items 10 and 11, 23 Sep 2026): all of
 *  them; nobody responded; one going, so backup may still be needed; two or
 *  more going. The customer on the last one: "once there is two people there
 *  we never ask for more as long as the two people on site are asking for
 *  more ... the members on site will decide when the call is covered, not
 *  the dispatcher." So it is a count of who is going, and nothing else --
 *  not Covered, which is the dispatcher's word.
 *
 *  Decided from `responder_count`, which the queue already carries, so the
 *  views cost no request and a call moves between them the moment somebody
 *  is logged. Remembered per browser, like the roster's sort. */
const VIEWS = [
  { key: 'all', label: 'All', fits: (_n: number) => true },
  { key: 'none', label: 'No response', tone: 'alarm', fits: (n: number) => n === 0 },
  { key: 'one', label: 'One going', tone: 'wait', fits: (n: number) => n === 1 },
  { key: 'two', label: 'Two going', tone: 'go', fits: (n: number) => n >= 2 },
] as const
type TileKey = (typeof VIEWS)[number]['key'] | 'drafts' | 'closed'
/** The four call tiles; "all" is lit exactly when the other three are. */
const CALL_TILES: TileKey[] = ['all', 'none', 'one', 'two']
const TILE_KEYS: TileKey[] = [...CALL_TILES, 'drafts', 'closed']
const TILES_PREF = 'dispatch.tiles'
/** What this browser had lit under the old one-tile-at-a-time rule, so
 *  nobody's screen changes on the deploy. */
function fromOldView(): TileKey[] {
  const old = readValue('dispatch.view', TILE_KEYS, 'all') as TileKey
  return old === 'all' ? [...CALL_TILES] : [old]
}

/** A draft this screen is writing (069): the form as it is now, what was
 *  last sent, and the one chain every create, save, heartbeat and release
 *  queues on, so they reach the server in order and each carries the version
 *  before it. */
type LiveDraft = {
  /** The form's React key. Stays the same when the server's id arrives, so
   *  the form is not redrawn under the dispatcher's hands. */
  key: string
  /** Null until the server has made it. */
  id: string | null
  version: number
  latest: IntakeDraft
  latestJson: string
  /** The last form the server accepted. */
  sentJson: string
  timer: number | null
  chain: Promise<void>
  /** The call is being opened from it: nothing is saved meanwhile. */
  busy: boolean
  /** A request for it is on its way. */
  saving: boolean
  /** No longer in the middle: saved, then let go, in the background. */
  left: boolean
  /** Nothing more to write: a call now, discarded, or a colleague's. */
  done: boolean
  /** The call was opened from this form. */
  opened: boolean
  /** The id it is created with -- the screen's own, so a create sent twice
   *  makes one draft. */
  newId: string
  /** Its release or discard is on its way: it is not resumed. */
  ending: boolean
  /** "Not being saved" has been said once already. */
  warned: boolean
}

/** A random id for a new draft. `randomUUID` needs a secure context, which
 *  this application is; the fallback is for a browser that lacks it. */
function uuid4(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  const h = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16))
  h[12] = '4'
  h[16] = ((parseInt(h[16], 16) & 3) | 8).toString(16)
  const s = h.join('')
  return `${s.slice(0, 8)}-${s.slice(8, 12)}-${s.slice(12, 16)}-${s.slice(16, 20)}-${s.slice(20)}`
}

/** JSON with every object's keys in order, so the same form always compares
 *  equal -- the database hands jsonb back with its keys reordered, and a plain
 *  `JSON.stringify` then saw a change in every draft merely opened. */
function stable(v: unknown): string {
  return JSON.stringify(v, (_k, x) => (x && typeof x === 'object' && !Array.isArray(x)
    ? Object.fromEntries(Object.keys(x).sort().map((k) => [k, (x as Record<string, unknown>)[k]]))
    : x))
}

/** A save refused because the draft moved on without this screen: somebody
 *  else has it open (423), it is a call now or was discarded (410), or it
 *  was changed since this screen read it (409). */
function draftLost(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 409 || e.status === 410 || e.status === 423)
}

/** A CALL CLOSED A MOMENT AGO, read-only, with Reopen (072).
 *
 *  The customer: "somebody calls in, he says, okay, it's okay ... we close it.
 *  And then he calls back, no, I decided I do need help." Reopen puts it back
 *  on the board exactly as it was -- the same members going -- and sends
 *  nothing (his Q1 A); any dispatcher whose role may close a call may reopen
 *  it, whoever closed it (his Q2 A). */
function ClosedCallView({ id, until, onReopened, onGone }: {
  id: string
  /** Until when it can be reopened, from the Closed list. */
  until: string | null
  onReopened: (id: string) => void
  onGone: () => void
}) {
  const [call, setCall] = useState<IncidentDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const mayReopen = can('calls.active.actions.close')

  useEffect(() => {
    let alive = true
    api.incident(id)
      .then((c) => {
        if (!alive) return
        // REOPENED ON ANOTHER SCREEN: it is a live call again; open it as one.
        if (c.status !== 'closed') { onReopened(id); return }
        setCall(c)
      })
      .catch((e) => {
        if (!alive) return
        // Past its window between the list and the click: Past calls' now.
        if (e instanceof ApiError && (e.status === 403 || e.status === 404)) onGone()
        else setError(e instanceof ApiError ? e.message : 'Could not open that call.')
      })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  async function reopen() {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await api.incidentReopen(id)
      onReopened(id)
    } catch (e) {
      // A COLLEAGUE REOPENED IT FIRST: no error -- it is open; go to it.
      const now = await api.incident(id).catch(() => null)
      if (now && now.status !== 'closed') { onReopened(id); return }
      setError(e instanceof ApiError ? e.message : 'Could not reopen it.')
      setBusy(false)
    }
  }

  if (error && !call) return <div className="wbody"><div className="notice">{error}</div></div>
  if (!call) return <div className="wbody dim">Loading…</div>
  const where = call.locations.at(-1)
  const closedBy = [...call.thread].reverse().find((e) => e.kind === 'closed')?.actor_label
  return (
    <div className="wbody">
      <div className="card ro-closed">
        <div className="ro-head">
          <h2>{call.number} · {call.nature_label}</h2>
          <span className="pill">Closed</span>
          <span className="spacer" />
          <button type="button" className="btn sm" onClick={onGone}>← Back to the list</button>
        </div>
        <div className="ro-bar">
          <div className="ro-txt">
            Closed at {hhmm(call.closed_at)}{closedBy ? <> by <b>{closedBy}</b></> : null}
            {call.resolution_label ? <> — <b>{call.resolution_label}</b></> : null}.
            {until && <><br />It can be reopened until <b>{hhmm(until)}</b>; after that it is under Reports / History.</>}
          </div>
          {mayReopen ? (
            <button type="button" className="btn ro-reopen" disabled={busy} onClick={reopen}>
              {busy ? 'Reopening…' : 'Reopen call'}
            </button>
          ) : (
            <span className="dim">Your role cannot reopen calls.</span>
          )}
        </div>
        {error && <div className="notice">{error}</div>}
        <dl className="kv ro-kv">
          <dt>Where</dt><dd>{where?.label ?? '—'}</dd>
          {where?.details && where.details !== where.label && (<><dt>Details</dt><dd>{where.details}</dd></>)}
          <dt>Caller</dt><dd>{call.anonymous ? 'Anonymous' : (call.caller_name ?? '—')}</dd>
          <dt>Going</dt>
          <dd>{call.responders.filter((r) => !r.released_at).map((r) => r.who).join(', ') || 'Nobody'}</dd>
        </dl>
        <div className="ro-thread">
          <ThreadRows thread={call.thread} />
        </div>
        <p className="dim ro-foot">Reopening puts it back on the board as it was, with the same
          members going. Nothing is sent — Re-broadcast or resend the details if needed.</p>
      </div>
    </div>
  )
}

/** Nothing typed, nothing pressed, and no caller it was started for. */
function blank(d: IntakeDraft): boolean {
  const f = (d.form ?? {}) as Record<string, unknown>
  const typed = (v: unknown) => typeof v === 'string' && v.trim() !== ''
  return !d.prefill
    && !['nature_label', 'location_label', 'caller_name', 'caller_phone', 'relationship_id']
      .some((k) => typed(f[k]))
    && Object.values(d.answers ?? {}).every((v) => !typed(v))
    && !typed(d.said) && !typed(d.locNote) && !typed(d.locDetails)
    && !typed(d.cross?.a) && !typed(d.cross?.b)
    && (d.others ?? []).every((o) => !typed(o.label) && !typed(o.phone))
    && d.urgency == null && d.callerUrgency == null && !d.anon && !d.area
    && (d.audience ?? []).length === 0 && !d.spot?.moved
}

/** HOW OLD A DRAFT IS, in whole minutes or hours -- it is waiting, not
 *  ticking, so no seconds. */
function age(from: string, now: Date): string {
  const mins = Math.max(0, Math.floor((now.getTime() - new Date(from).getTime()) / 60000))
  if (mins < 60) return `${mins}m`
  return `${Math.floor(mins / 60)}h${String(mins % 60).padStart(2, '0')}`
}

/** After this a draft is an "old draft" in amber (his Q2 A). The server's
 *  `call_drafts.OLD_HOURS`. */
const OLD_DRAFT_MS = 2 * 60 * 60 * 1000

/** One draft on the list: what it is so far, and who has it. */
function DraftCard({ draft, now, open, onPick }: {
  draft: CallDraft; now: Date; open: boolean; onPick: () => void
}) {
  const old = now.getTime() - new Date(draft.created_at).getTime() >= OLD_DRAFT_MS
  return (
    <button className={`qcall dr-card${old ? ' dr-old' : ''}`} aria-selected={open} onClick={onPick}>
      <div className="t">{age(draft.created_at, now)}<small>Draft</small></div>
      <div>
        <div className="n">{draft.nature_label ?? 'No nature yet'}</div>
        <div className="a">
          {draft.location_label ?? 'No address yet'}
          {draft.caller_label ? ` · caller ${draft.caller_label}` : ''}
        </div>
        <div className="flags">
          {old && <span className="pill wait">Old draft</span>}
          {draft.held_by_me
            ? <span className="pill dr-pill">Yours, open now</span>
            : draft.held_by_label
              ? <span className="pill dr-pill">Being filled by {draft.held_by_label}</span>
              : <span className="pill dr-pill">Started by {draft.created_by_label}</span>}
        </div>
      </div>
    </button>
  )
}

/** ITS OWN LIGHT TINT FOR EVERY OPEN CALL (the customer, 22 Sep 2026): "when
 *  a dispatcher looks, he knows, okay, I'm in the middle of the green call,
 *  I'm in the middle of the red call ... not need to read the nature of call
 *  or the address". A memory aid, so it carries no meaning and is stored
 *  nowhere: it is chosen from the call number, which makes INC-23 the same
 *  colour on every dispatcher's screen for its whole life. Eight tints, and
 *  two neighbours in the list are never the same one -- consecutive numbers
 *  never collide, and the rare pair that would is nudged along.
 *
 *  THE RED LEFT EDGE KEEPS ITS MEANING. The tint sits behind it; "needs
 *  attention" is still the border and the timer, as before. */
const TINTS = 8

function tints(calls: Incident[]): number[] {
  const out: number[] = []
  for (const c of calls) {
    let hue = (parseInt(c.number.replace(/\D/g, ''), 10) || 0) % TINTS
    if (out.length && hue === out[out.length - 1]) hue = (hue + 1) % TINTS
    out.push(hue)
  }
  return out
}

function QueueCall({
  call, now, selected, onPick, onCollapse, hue,
}: {
  call: Incident; now: Date; selected: boolean; onPick: () => void
  /** Double-click on the call that is OPEN: back to "No call selected". */
  onCollapse: () => void
  /** Which of the eight tints, from `tints()`. */
  hue: number
}) {
  // WHETHER THIS ROW WAS THE OPEN ONE BEFORE THE FIRST CLICK OF THE PAIR. A
  // double-click is two clicks and then the double-click event, and by the
  // third the first click has already opened this row -- so `selected` reads
  // true for ANY row double-clicked, and a dispatcher double-clicking a call
  // to open it would watch it open and vanish. The customer, 22 Sep 2026:
  // "when I double click on one, it should collapse it"; a different call
  // just opens.
  const wasOpen = useRef(false)
  // The heat comes from the ORGANIZATION's own windows now, not from numbers
  // invented here. The old thresholds -- 7 and 10 minutes, hard-coded -- meant
  // a coordinator who set 5 minutes on the Operations screen got 7, and one
  // who set 20 got 10, with nothing anywhere saying so.
  const heat = ESCALATION[call.escalation]?.heat ?? ''

  const flag = ESCALATION[call.escalation]

  return (
    <button
      className={`qcall ${heat} hue-${hue + 1}${call.mark_colour ? ` marked mark-${call.mark_colour}` : ''}`}
      aria-selected={selected}
      onClick={() => { wasOpen.current = selected; onPick() }}
      onDoubleClick={() => { if (wasOpen.current) onCollapse() }}
      title={selected ? 'Double-click to collapse' : undefined}
    >
      {/* A CAME-IN TIME NOT KNOWN (083) has no age: its column is the day. */}
      <div className="t">{call.called_at_unknown ? '—' : elapsed(call.called_at, now)}<small>Open</small></div>
      <div>
        <div className="n">
          {/* Before the name, as on the nature list, and the server's own
              stored value -- see `Incident.call_code`. */}
          {call.call_code && <span className="callcode">{call.call_code}</span>}
          {call.call_code && ' '}
          {call.nature_label}
        </div>
        <div className="a">{call.location ?? '—'}</div>
        {/* The reason it needs attention, said in words and first. A colour on
            the edge of a card is a hint; at 3am the dispatcher needs the
            sentence. */}
        {flag && (
          <div className={`escalated ${call.escalation}`}>
            {flag.label}
            {call.escalation_seconds !== null && (
              <span className="mono"> {Math.round(call.escalation_seconds / 60)}m</span>
            )}
          </div>
        )}
        <div className="flags">
          {/* A SAFETY ANSWER, FIRST AND LOUD. It is usually carried by the
              priority raise as well, but a dispatcher may lower that, and a
              call opened with a chosen urgency is never raised at all — in both
              of those the answer is still the reason to look at this one
              first. Read from the answer on the server, never inferred from
              priority 1. */}
          {call.has_alert && <span className="pill alarm">Flagged</span>}
          {call.anonymous && <span className="pill alarm">Anonymous</span>}
          {call.caller_count > 1 && <span className="pill wait">{call.caller_count} callers</span>}
          {call.caller_urgency != null && call.caller_urgency <= 2 && (
            <span className="pill alarm">
              caller {PRIORITIES.find((p) => p.v === call.caller_urgency)?.label.toLowerCase()}
            </span>
          )}
          {call.responder_count === 0 ? (
            <span className="pill alarm">No response</span>
          ) : call.on_scene_at ? (
            <span className="pill go">On scene</span>
          ) : (
            <span className="pill go">{call.responder_count} responding</span>
          )}
          {call.relocated_count > 0 && <span className="pill wait">Moved</span>}
          {call.caller_on_scene === false && <span className="pill">Caller gone</span>}
          {call.logged_afterwards && <span className="pill lgc-own">Logged afterwards</span>}
        </div>
      </div>
    </button>
  )
}

/** "The call moved": one box for the new address, with the same suggestions
 *  the Take a call screen offers -- the organization's own address book first,
 *  then the map -- and who said so. Picking a suggestion carries its spot as
 *  the call's pin; typing an address the map does not know still moves the
 *  call, exactly as typed, and the place is looked up afterwards.
 *
 *  Kept deliberately smaller than the intake's address box: no cross streets
 *  and no area here. Those are corrected on the card afterwards, where they
 *  already have their own controls. */
function MoveForm({ onCancel, onMoved }: {
  onCancel: () => void
  onMoved: (body: {
    label: string; place_id?: string | null
    reported_by: 'caller' | 'responder' | 'dispatcher'
    latitude?: number | null; longitude?: number | null
    note?: string | null
  }) => Promise<void>
}) {
  const [label, setLabel] = useState('')
  const [note, setNote] = useState('')
  const [who, setWho] = useState<'caller' | 'responder' | 'dispatcher'>('caller')
  const [picked, setPicked] = useState<{
    label: string; place_id: string | null
    latitude: number | null; longitude: number | null
  } | null>(null)
  const [offered, setOffered] = useState<
    Awaited<ReturnType<typeof api.placeSuggest>>['suggestions']
  >([])
  const [busy, setBusy] = useState(false)
  const session = useRef(Math.random().toString(36).slice(2))

  useEffect(() => {
    const typed = label.trim()
    if (typed.length < 2 || picked?.label === typed) { setOffered([]); return }
    let alive = true
    const t = setTimeout(() => {
      api.placeSuggest(typed, session.current)
        .then((found) => { if (alive) setOffered(found.suggestions) })
        .catch(() => { if (alive) setOffered([]) })
    }, 180)
    return () => { alive = false; clearTimeout(t) }
  }, [label, picked])

  async function take(s: (typeof offered)[number]) {
    // PICKED BEFORE ANYTHING IS AWAITED, so the suggestion effect above sees
    // the label as settled and does not ask the map again -- a billed
    // request, and a list flickering back open -- during the resolve.
    setPicked({ label: s.label, place_id: s.place_id, latitude: s.latitude, longitude: s.longitude })
    setLabel(s.label)
    setOffered([])
    // A map suggestion with an id and no point is resolved now, inside the
    // same billing session, the way the intake does it.
    if (s.latitude === null && s.source === 'map' && s.provider_place_id) {
      try {
        const at = await api.placeResolveSuggestion(s.provider_place_id, session.current)
        if (at.found && at.latitude !== null && at.longitude !== null) {
          setPicked({ label: s.label, place_id: s.place_id, latitude: at.latitude, longitude: at.longitude })
        }
      } catch { /* the label still moves the call; the place is looked up later */ }
    }
    // A map pick spends the billing session; the next typing starts a new one.
    if (s.source === 'map') session.current = Math.random().toString(36).slice(2)
  }

  async function save() {
    const typed = label.trim()
    if (!typed || busy) return
    setBusy(true)
    const same = picked && picked.label === typed
    try {
      await onMoved({
        label: typed,
        place_id: same ? picked.place_id : null,
        reported_by: who,
        latitude: same ? picked.latitude : null,
        longitude: same ? picked.longitude : null,
        note: note.trim() || null,
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="bd moveform" style={{ borderTop: '1px solid var(--line-soft)' }}>
      <div className="lbl" style={{ marginBottom: 6 }}>The call moved — where is it now?</div>
      <input
        autoFocus
        className="orgpick"
        style={{ width: '100%', maxWidth: 'none' }}
        value={label}
        placeholder="New address, or a street and a cross street"
        aria-label="New address"
        onChange={(e) => { setLabel(e.target.value); setPicked(null) }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); save() }
          if (e.key === 'Escape' && !busy) onCancel()
        }}
      />
      {offered.length > 0 && (
        <div className="suggest" role="listbox">
          {offered.map((s, i) => (
            <button type="button" className="sug" key={`${s.label}-${i}`} role="option"
                    onClick={() => take(s)}>
              {s.label}
              <span className="lbl" style={{ marginLeft: 8 }}>
                {s.source === 'book' ? 'been there' : 'map'}
              </span>
            </button>
          ))}
        </div>
      )}
      <input
        className="orgpick"
        style={{ width: '100%', maxWidth: 'none', marginTop: 8 }}
        maxLength={400}
        value={note}
        placeholder="Notes about the new location (optional)"
        aria-label="Notes about the new location"
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="chips" style={{ margin: '10px 0' }}>
        {(['caller', 'responder', 'dispatcher'] as const).map((w) => (
          <button key={w} type="button" className={`chip ${who === w ? 'on' : ''}`}
                  onClick={() => setWho(w)}>
            {w === 'caller' ? 'The caller says' : w === 'responder' ? 'A responder says' : 'Dispatcher'}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn primary" disabled={!label.trim() || busy} onClick={save}>
          {busy ? 'Moving…' : 'Move the call here'}
        </button>
        <button className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
        <span className="footnote" style={{ margin: 0 }}>
          The old address stays on the record. Responders going are asked if they still are.
        </span>
      </div>
    </div>
  )
}

/** THE WORKING CALL IN THREE TABS (the customer, 22 Sep 2026): "one tab
 *  should be the information collected from the caller. The other tab should
 *  be everything about broadcasting, who took the call and everything else.
 *  And the third tab should be the closing details ... it will be easier for
 *  the user not to need to scroll so long and it will also make it more
 *  friendly if it will be open on a tablet or on a phone."
 *
 *  THE PANELS STAY MOUNTED and are hidden, never unmounted: the answer boxes,
 *  the narrative box and the closing chips hold what is being typed, and a
 *  dispatcher who glances at the Dispatch tab mid-sentence must find his
 *  sentence where he left it. */
type WorkTab = 'caller' | 'dispatch' | 'close'

function WorkArea({
  call, hue, members, resolutions, natures, seat, act, onClosed, onCollapse,
}: {
  call: IncidentDetail
  /** The nature list, for "+ Add another nature" (076). */
  natures: ListItem[]
  /** The call's tint, the same one its card on the left is drawn in. */
  hue: number
  /** The roster, for the picker's full list. */
  members: Member[]
  resolutions: ListItem[]
  seat: RadioSeat | null
  act: (fn: () => Promise<unknown>) => Promise<void>
  onClosed: () => void
  /** Back to the list on the left, still open. */
  onCollapse: () => void
}) {
  const [closing, setClosing] = useState(false)
  /** "Correct this call" is open on this call (079), and -- after a save that
   *  changed where it is with somebody still going -- that he still has the
   *  old address (his Q2 A: nothing is sent; one press resends). */
  const [correcting, setCorrecting] = useState(false)
  const [oldAddress, setOldAddress] = useState(false)
  /** WHICH TAB OPENS WITH THE CALL (the customer's Q1 A): Caller while it
   *  has not been broadcast -- the dispatcher is still collecting -- and
   *  Dispatch once it has, which is where the work is from then on. Chosen
   *  once per call; pressing a tab afterwards is his choice and stays. */
  const [tab, setTab] = useState<WorkTab>(() => (call.broadcast_at ? 'dispatch' : 'caller'))
  useEffect(() => {
    setTab(call.broadcast_at ? 'dispatch' : 'caller')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call.id])
  /** ONE BROADCAST PER PRESS. Two quick presses used to send every member the
   *  same emergency twice -- and the likeliest moment for a second press is a
   *  first one that seemed to do nothing. */
  const [sending, setSending] = useState(false)
  const [sends, setSends] = useState<Awaited<ReturnType<typeof api.incidentBroadcasts>>>([])
  /** Who the next Broadcast or Re-broadcast goes to. Empty is everyone on duty,
   *  which is where it starts on every call. */
  const [audience, setAudience] = useState<string[]>([])
  const audiences = useAudiences()
  const aimedAtNobody = audience.length > 0 && reachesNobody(audiences, audience)
  /** Which handover message is on its way, one at a time. */
  const [handing, setHanding] = useState<string | null>(null)
  /** What became of the last one, in words beside the buttons -- green when it
   *  went, amber when it did not or somebody could only be told by radio. */
  const [handNote, setHandNote] = useState<{ ok: boolean; text: string } | null>(null)
  /** "Send him the full address & details" (078): on, as it always was --
   *  OFF on a call logged afterwards, which was never meant to send. Unticked,
   *  the next member logged is sent nothing, and it goes back to that start
   *  after him and on every other call, so a tick somebody forgot never
   *  leaves the next member without the address. */
  const sendStart = !call.logged_afterwards
  const [sendDetails, setSendDetails] = useState(sendStart)
  /** Which responder's arrival is being written, so a double press is one. */
  const [arriving, setArriving] = useState<string | null>(null)
  /** "Take MT-25 off this call?" -- asked under his row first (080), with an
   *  optional why. One at a time. */
  const [removing, setRemoving] = useState<{ id: string; reason: string; busy: boolean } | null>(null)
  const [resolutionId, setResolutionId] = useState('')
  const [resolutionLabel, setResolutionLabel] = useState('')
  const [outcome, setOutcome] = useState('')

  const going = call.responders.filter((r) => !r.released_at)
  /** The rows under Responding: everyone going, and anyone NO LONGER going who
   *  was marked arriving -- because that arrival may be the call's On scene
   *  time, and if it was pressed on the wrong name this row is the only place
   *  it can be taken back. */
  const listed = call.responders.filter((r) => r.removed_at || !r.released_at || r.on_scene_at)
  // Kept locally so adding a note updates in place rather than waiting for the
  // next poll of the whole incident.
  const [place, setPlace] = useState(call.place)
  useEffect(() => { setPlace(call.place) }, [call.place])

  // The narrative composer. It sits under the thread rather than behind a
  // dialog because it is written DURING the call, between sentences on the
  // telephone, and a dialog is a thing to open, aim at and dismiss.
  const [note, setNote] = useState('')
  const [posting, setPosting] = useState(false)
  /** "Call me at home" said mid-call: the half-typed number, or null when the
   *  small form is shut. Keyed to the call below, like the narrative. */
  const [newNumber, setNewNumber] = useState<
    { label: string; phone: string; save: BookSave; name: string } | null>(null)
  /** "The call moved": the form is open, and what has been picked so far. */
  const [moving, setMoving] = useState(false)
  /** The location note being corrected, or null when it is not. */
  const [noteEdit, setNoteEdit] = useState<string | null>(null)
  const [numberBusy, setNumberBusy] = useState(false)
  /** Why the number just added is not in the phone book, when it is not. */
  const [numberNote, setNumberNote] = useState<string | null>(null)

  // A DRAFT DOES NOT FOLLOW THE DISPATCHER TO THE NEXT CALL. This component is
  // not remounted when a different call is picked -- there is no `key` on it --
  // so without this the half-typed sentence about one incident stays in the box
  // underneath another incident's heading, and the next press of Enter files it
  // against the wrong record. That is the same wrong-record failure the intake
  // prefill is cleared on every exit to prevent, and losing a draft on a
  // deliberate switch is the cheaper half of it by a wide margin. Keyed on the
  // id and not the object, so the re-read after every write leaves it alone.
  useEffect(() => { setNote('') }, [call.id])
  // Nor does a choice of audience or a sentence about another call's handover.
  useEffect(() => {
    setAudience([]); setHandNote(null); setNewNumber(null); setMoving(false); setNoteEdit(null)
    setSendDetails(sendStart); setNumberNote(null); setRemoving(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call.id])
  // THE CLOSE TAB IS ALWAYS DRAWN NOW, so what was chosen on the last call
  // must not still be chosen on this one: a resolution picked on call A and
  // a "Confirm and close" left armed would close call B with A's words.
  useEffect(() => {
    setClosing(false); setResolutionId(''); setResolutionLabel(''); setOutcome('')
    setCorrecting(false); setOldAddress(false)
  }, [call.id])

  /** Another number to reach the caller on. Through `act`, so the thread and
   *  the list re-read together; the box stays filled if it is refused. */
  async function addNumber() {
    if (!newNumber || !newNumber.phone.trim() || numberBusy) return
    // Enter in the number box as well as the button: someone else needs a name.
    if (newNumber.save === 'someone' && !newNumber.name.trim()) return
    const { label, phone, save, name } = newNumber
    setNumberBusy(true)
    setNumberNote(null)
    await act(async () => {
      const rows = await api.incidentCallbackAdd(call.id, label.trim() || null, phone.trim(),
                                                 { save, name: name.trim() || null })
      // WHY IT IS NOT IN THE PHONE BOOK, when it is not (review).
      setNumberNote(rows.find((r) => r.book_note)?.book_note ?? null)
      // SHUT ONLY IF IT STILL HOLDS WHAT WAS SENT. A digit corrected while it
      // said "Adding…" is the next number to add, not typing to throw away.
      setNewNumber((cur) => (cur && cur.label === label && cur.phone === phone ? null : cur))
    })
    setNumberBusy(false)
  }

  async function removeNumber(n: { id: string; label: string | null; phone_raw: string }) {
    if (!confirm(`Remove ${n.label ?? 'this number'} (${n.phone_raw}) from the call?`)) return
    setNumberBusy(true)
    await act(() => api.incidentCallbackRemove(call.id, n.id))
    setNumberBusy(false)
  }

  /** Say what reached whom, how, and when -- "Full address & details sent to
   *  U - 20 by text and email · 13:18". The first version said "sent to 1
   *  member(s)" in grey, and the customer pressed the button three times
   *  believing nothing had happened, while each press had gone out twice. */
  function reached(
    what: string, to: string[] | undefined, by: string[] | undefined,
    radioOnly: string[] | undefined,
  ) {
    const names = to && to.length ? to.join(', ') : 'the member going'
    const how = by && by.length ? ` by ${by.join(' and ')}` : ''
    const when = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
    const tail = radioOnly && radioOnly.length
      ? ` ${radioOnly.join(', ')} has no text or email set up — tell them by radio.`
      : ''
    return { ok: !tail, text: `${what} sent to ${names}${how} · ${when}.${tail}` }
  }

  /** He arrived -- or, taken back, he did not. One responder at a time. */
  async function arrived(responderId: string, who: string, undo: boolean) {
    if (undo && !confirm(`Take back ${who}'s arrival? Only do this if it was pressed by mistake.`)) {
      return
    }
    setArriving(responderId)
    await act(() => api.incidentResponderOnScene(call.id, responderId, undo))
    setArriving(null)
  }

  /** TAKE OFF A MEMBER LOGGED BY MISTAKE (080). He stays on the record,
   *  crossed out; nothing is sent to him unless the organization chose to tell
   *  him and he had been sent the details. Through `act`, so a refusal lands
   *  in the banner and the call re-reads with him crossed out. */
  async function removeResponder(r: { id: string; who: string }) {
    if (!removing || removing.id !== r.id || removing.busy) return
    const reason = removing.reason.trim() || null
    setRemoving({ ...removing, busy: true })
    const did = { done: false }
    await act(async () => {
      const out = await api.incidentResponderRemove(call.id, r.id, reason)
      did.done = true
      const told = out.told
      const sent = told?.told
        ? ` He was told he is no longer on it${told.by?.length ? ` by ${told.by.join(' and ')}` : ''}.`
        : told?.why === 'radio_only'
          ? ' He has no text or email set up — tell him by radio.'
          : told?.why === 'failed'
            ? ' The message telling him could not be sent — tell him by radio.'
            : ' Nothing was sent to him.'
      setHandNote({
        ok: !told || told.told || told.why === 'never_sent',
        text: `${out.who} taken off the call.${sent}`
          + (out.promoted ? ` ${out.promoted} is now the primary.` : ''),
      })
    })
    setRemoving(did.done ? null : (cur) => (cur && cur.id === r.id ? { ...cur, busy: false } : cur))
  }

  /** The gate codes and site notes, or the callback number, to whoever is going.
   *
   *  ONLY ON A PRESS, and the callback number asks first: the customer's rule
   *  is that "a normal responder does not get the person's number" and the
   *  dispatcher calls back. Through `act`, so a refusal lands in the banner and
   *  the thread re-reads with the line saying it was sent. */
  async function sendToGoing(what: 'site' | 'callback' | 'details') {
    const names = going.map((r) => r.who).join(' and ')
    if (what === 'callback'
        && !confirm(`Send the caller's number to ${names}? Normally you call back yourself.`)) {
      return
    }
    setHanding(what)
    setHandNote(null)
    await act(async () => {
      const r = await api.incidentSend(call.id, what)
      const said = what === 'site' ? 'Gate codes & site notes'
        : what === 'callback' ? 'Callback number' : 'Full address & details'
      setHandNote(reached(said, r.to, r.by, r.radio_only))
    })
    setHanding(null)
  }

  /** Put what was typed into the running record of the call.
   *
   *  THE BOX IS EMPTIED ONLY ONCE THE WRITE HAS LANDED, which is why the clear
   *  sits INSIDE the function handed to `act`: a POST that throws never reaches
   *  it, and `act`'s own catch puts the reason in the banner with the words
   *  still on screen to retry or copy out. The responder picker three boxes up
   *  is the lesson -- it reset its select BEFORE the request went out, so a 403
   *  took the dispatcher's choice with it, mid-call, when there is no time to
   *  work out what was lost.
   *
   *  It goes through `act` rather than calling the client directly so that a
   *  note lands exactly the way an acceptance does. `POST .../notes` answers
   *  `{ok: true}` and NOT the updated thread, so without that re-read the
   *  dispatcher types a sentence, sees nothing appear above it, and types it
   *  again. */
  async function addNote() {
    const text = note.trim()
    if (!text || posting) return
    setPosting(true)
    await act(async () => {
      await api.incidentNote(call.id, text)
      // Only what was actually sent is taken away. A dispatcher does not stop
      // typing because a request is in flight, and blanking the box outright
      // would swallow whatever he wrote during that window -- the same lost
      // typing this whole function is shaped around, arriving by the back door.
      setNote((current) => (current.trim() === text ? '' : current))
    })
    // `act` turns a failure into the banner rather than rethrowing, so this
    // runs on both paths and the button can never be left spinning for good.
    setPosting(false)
  }

  // "It was sent" and "it arrived" are different claims. Sending happens
  // behind the dispatcher, so this polls briefly to catch the outcome landing
  // rather than leaving him to assume everyone was reached.
  useEffect(() => {
    let alive = true
    const load = () =>
      api.incidentBroadcasts(call.id).then((b) => alive && setSends(b)).catch(() => {})
    load()
    const t = setInterval(load, 5000)
    return () => { alive = false; clearInterval(t) }
  }, [call.id, call.broadcast_at])

  const steps: [string, string | null][] = [
    ['Call in', call.called_at_unknown ? 'not known' : hhmm(call.called_at)],
    ['Broadcast', hhmm(call.broadcast_at)],
    ['Accepted', hhmm(call.accepted_at)],
    ['On scene', hhmm(call.on_scene_at)],
    ['Covered', hhmm(call.covered_at)],
  ]
  const firstBlank = steps.findIndex((s) => !s[1])
  /** Room for the summary beside the tabs: measured on the call's own
   *  width, which the columns either side of it set, never the window's. */
  const [splitRef, roomy] = useRoomy(900)

  return (
    <>
      <div className={`wh hue-${hue + 1}${call.mark_colour ? ` marked mark-${call.mark_colour}` : ''}`}>
        <div>
          <div className="num">
            {call.number} · {call.called_at_unknown ? 'time not known' : hhmm(call.called_at)} · {call.source}
            {call.logged_afterwards && (
              <> <span className="pill lgc-own">Logged afterwards · nothing has been sent</span></>
            )}
          </div>
          <h1>
            {/* THE CODE HE SAYS ON THE AIR, LARGE AND FIRST. Re-read from the
                server after every answer saved below, because the server is
                what composes and freezes it. */}
            {call.call_code && (
              <span className="callcode big">{call.call_code}</span>
            )}
            {call.call_code && ' '}
            {call.nature_label}
          </h1>
          <div className="sub">
            {/* THE BIG MARK (061): the name is here, and beside it the reason
                it goes to nobody. */}
            {call.anonymous && <span className="anonpill">Anonymous · not to be given out</span>}
            {call.caller_name ?? 'Unknown caller'}
            {/* THE CALLER'S OWN URGENCY (062), beside the caller, in the
                intake's word, so "Normal call, Urgent caller" reads at a
                glance. Nothing when it was not said. */}
            {call.caller_urgency != null && (
              <span className={`pill ${call.caller_urgency <= 2 ? 'alarm' : call.caller_urgency === 3 ? '' : 'wait'}`}
                    style={{ marginLeft: 8 }}>
                caller {PRIORITIES.find((p) => p.v === call.caller_urgency)?.label.toLowerCase()}
              </span>
            )}
            {call.relationship_label && ` · calling for ${call.relationship_label.toLowerCase()}`}
            {call.caller_on_scene === false && (
              <span style={{ color: 'var(--wait)' }}> · not staying on scene</span>
            )}
          </div>
          {/* THE SOLID MARK, chosen here and shown on the card too (059). Six
              colours and a cross for none. Through `act`, so the queue and
              the header redraw from the same server answer. */}
          {call.status !== 'closed' && (
            <div className="marker" role="radiogroup" aria-label="Mark this call">
              <span className="lbl">Mark this call</span>
              <button type="button" role="radio" aria-checked={!call.mark_colour}
                      className={`sw none ${!call.mark_colour ? 'on' : ''}`} title="No mark"
                      onClick={() => act(() => api.incidentMark(call.id, null))}>
                ×
              </button>
              {MARK_COLOURS.map((m) => (
                <button key={m} type="button" role="radio" aria-checked={call.mark_colour === m}
                        className={`sw mark-${m} ${call.mark_colour === m ? 'on' : ''}`}
                        title={m} aria-label={m}
                        onClick={() => act(() => api.incidentMark(call.id, m))} />
              ))}
            </div>
          )}
        </div>
        <div className="right">
          {/* BACK TO THE LIST, STILL OPEN. The customer: "a button when the
              call is open in the middle to collapse it to the side to be able
              to open another one". Every open call is already on the left;
              this only stops it being the one in the middle. */}
          {/* CORRECT THIS CALL (079): the whole of it, for the tick. */}
          {can('calls.past.correct') && call.status !== 'closed' && !correcting && (
            <button className="btn sm ccor-open" onClick={() => { setOldAddress(false); setCorrecting(true) }}>
              Correct this call
            </button>
          )}
          <button className="btn sm collapsebtn" onClick={onCollapse}
                  title="Put this call back in Open calls. Nothing about it changes.">
            ⇤ Collapse
          </button>
          {/* ON A PHONE THE QUEUE IS BEHIND THIS CALL, not beside it, so the
              same action reads as going back to the list. */}
          <button className="btn sm backq" onClick={onCollapse}>
            ← Open calls
          </button>
          {/* The dispatcher's own radio audio is filed to whichever call is
              selected — a dispatcher may be working two at once, and the fleet
              recording alone cannot say which. */}
          <div className="onair">
            <span className="d" />
            <span>Radio →</span>
            <b>{call.number}</b>
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-faint)' }}>
            {seat ? `Your transmissions on ${seat.label} file here` : 'No radio connection'}
          </div>
        </div>
      </div>

      {/* THE FIVE TIME STAMPS UNDER THE HEADER, ON EVERY TAB (Q2 B): the age
          of a call is always in view, whichever tab the work is on. */}
      <div className="tl slim">
        {steps.map(([label, value], i) => (
          <div className={`st ${value ? 'done' : i === firstBlank ? 'now' : ''}`} key={label}>
            <div className="lb">{label}</div>
            <div className="vl">{value ?? '— —'}</div>
          </div>
        ))}
      </div>

      {/* THE CALL AT A GLANCE, ON EVERY TAB (style B, the customer's Q1 B,
          23 Oct 2026): "it's so much information, it's too hard to see". What
          a responder needs first -- before anyone goes, the caller, where,
          the answers, who is going -- never sits behind a tab. */}
      {oldAddress && (
        <div className="banner ccor-old" role="status">
          Corrected. Whoever is going still has the old address — nothing was sent.
          <button type="button" className="btn sm" disabled={handing !== null}
                  onClick={async () => { await sendToGoing('details'); setOldAddress(false) }}>
            {handing === 'details' ? 'Sending…' : 'Resend full address & details'}
          </button>
          <button type="button" className="ghost tiny" onClick={() => setOldAddress(false)}>OK</button>
        </div>
      )}
      {correcting && (
        <div className="wbody">
          <CorrectCall
            key={call.id}
            call={call}
            onCancel={() => setCorrecting(false)}
            onSaved={async (r) => {
              setCorrecting(false)
              await act(async () => {})
              if (r.where_changed && (r.going ?? 0) > 0) setOldAddress(true)
            }}
          />
        </div>
      )}
      <div className={`wsplit ${roomy ? 'roomy' : 'tight'}`} ref={splitRef} hidden={correcting}>
      {roomy && (
        <CallSummary call={call} place={place} going={going} onLocated={setPlace}
                     onCallerTab={tab !== 'caller' ? () => setTab('caller') : null} />
      )}
      <div className="wmain">
      <div className="tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'caller'}
                className={`tab ${tab === 'caller' ? 'on' : ''}`} onClick={() => setTab('caller')}>
          <span className="k">1 · Caller</span>
          {call.has_alert && <span className="badge alarm">flagged</span>}
        </button>
        <button type="button" role="tab" aria-selected={tab === 'dispatch'}
                className={`tab ${tab === 'dispatch' ? 'on' : ''}`} onClick={() => setTab('dispatch')}>
          <span className="k">2 · Dispatch</span>
          {going.length
            ? <span className="badge go">{going.length} responding</span>
            : call.broadcast_at
              ? <span className="badge alarm">no response</span>
              : <span className="badge wait">not sent</span>}
        </button>
        <button type="button" role="tab" aria-selected={tab === 'close'}
                className={`tab ${tab === 'close' ? 'on' : ''}`} onClick={() => setTab('close')}>
          <span className="k">3 · Close</span>
          {call.covered_at && <span className="badge go">covered</span>}
        </button>
      </div>

      {/* BEFORE ANYONE GOES, ON EVERY TAB (Q3 A). The card itself, with its
          buttons, is on the Caller tab; the other two carry one line that
          says whether there is anything to read and takes you there. A gate
          code or a dog in the yard must not hide behind a tab. UNDER the
          tab row, not above it, so pressing a tab never moves the tab row
          under the pointer. */}
      {/* THE SUMMARY BESIDE EVERY TAB SAYS IT NOW, in full; this strip is
          kept for a screen too narrow to show the summary. */}
      {tab !== 'caller' && (
        <button type="button" className="beforestrip narrowonly" onClick={() => setTab('caller')}>
          <span className="lbl">Before anyone goes</span>
          {(() => {
            const notes = place?.notes.length ?? 0
            const codes = !!(place?.entry_codes || '').trim()
            if (!place) return <span className="none">No address on record yet.</span>
            if (!notes && !codes) return <span className="none">Nothing recorded about this address yet.</span>
            const parts = [
              notes ? `${notes} site note${notes === 1 ? '' : 's'}` : '',
              codes ? 'entry codes' : '',
            ].filter(Boolean).join(' · ')
            return <span className="has">{parts} — read them on the Caller tab</span>
          })()}
        </button>
      )}

      <div className="wbody">
        {/* ---- 1 · Caller: what the caller told us ---------------------- */}
        <div className="wpanel" hidden={tab !== 'caller'}>
        {/* At the top, in full, before anything else on the call. Section 06:
            these surface prominently rather than sitting in the call list,
            because the failure mode is a responder finding the dog himself. */}
        <div className="wcols2">
        <div className="wcol">
        <SiteNotes
          place={place}
          onChanged={setPlace}
          onCrossStreets={(a, b) => act(() => api.incidentCrossStreets(call.id, a, b))}
        />

        {/* THE MAP IS IN THE SUMMARY, on every tab -- and HERE when there is
            no room for the summary, or the address could not be checked on a
            map at all on a narrow screen. One measurement decides both. */}
        {!roomy && <PlaceMap place={place} onLocated={setPlace} pinned={call.pinned} />}

        <div className="wstack">
          <div className="box">
            <h4>Caller</h4>
            <div className="bd">
              {/* ANONYMOUS (061): everything is here, and the card says loudly
                  that none of it leaves the room. The messages enforce it. */}
              {call.anonymous && (
                <div className="anonnote" role="note">
                  <b>Anonymous caller.</b> Keep every detail in house: not in a
                  text, not over the air, not to a responder. The system will
                  not send his name or number.
                </div>
              )}
              <dl className="kv">
                <dt>Name</dt><dd>{call.caller_name ?? '—'}</dd>
                {/* EVERY NUMBER TO REACH HIM ON, where it is dialled from. The
                    customer: "we should have the number from where he called
                    and ... another numbers where to call". The one he called
                    from was only ever on the Take a call form. */}
                <CallbackRows
                  callerPhone={call.caller_phone}
                  numbers={call.callback_numbers ?? []}
                  onRemove={removeNumber}
                  busy={numberBusy}
                  callerName={call.anonymous ? null : call.caller_name}
                  // OUTSIDE `act`, which turns a refusal into the page's banner:
                  // the row's own form must hear it and stay open (review).
                  onFile={can('phonebook.add')
                    ? async (n, save, name) => {
                      await api.incidentCallbackFile(call.id, n.id, save, name)
                      await act(async () => {})
                    }
                    : undefined}
                />
                <dt>Calling for</dt><dd>{call.relationship_label ?? '—'}</dd>
                <dt>On scene</dt>
                <dd>{call.caller_on_scene === false
                  ? <em>No — left the scene</em>
                  : call.caller_on_scene ? 'Yes' : '—'}</dd>
                {/* HOW URGENTLY HE NEEDS HELP (062), changed here mid-call and
                    said in the thread. Independent of the call's priority. */}
                <dt>Caller urgency</dt>
                <dd>
                  <div className="chips">
                    {PRIORITIES.map((p) => (
                      <button type="button" key={p.v}
                              className={`chip ${call.caller_urgency === p.v ? 'on' : ''}`}
                              disabled={call.status === 'closed'}
                              onClick={() => act(() => api.incidentCallerUrgency(
                                call.id, call.caller_urgency === p.v ? null : p.v))}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                </dd>
              </dl>
              {newNumber ? (
                <div className="addnumber">
                  <input
                    autoFocus
                    maxLength={40}
                    value={newNumber.label}
                    placeholder="Home, wife…"
                    aria-label="Whose number"
                    onChange={(e) => setNewNumber({ ...newNumber, label: e.target.value })}
                  />
                  <PhoneInput
                    maxLength={40}
                    value={newNumber.phone}
                    placeholder="Number"
                    aria-label="Number"
                    onChange={(e) => setNewNumber({ ...newNumber, phone: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                        e.preventDefault()
                        addNumber()
                      }
                    }}
                  />
                  <button className="btn sm"
                          disabled={!newNumber.phone.trim() || numberBusy
                            // "As someone else" needs their name (review).
                            || (newNumber.save === 'someone' && !newNumber.name.trim())}
                          onClick={addNumber}>
                    {numberBusy ? 'Adding…' : 'Add'}
                  </button>
                  <button className="ghost tiny" disabled={numberBusy}
                          onClick={() => setNewNumber(null)}>
                    Cancel
                  </button>
                </div>
              ) : call.status !== 'closed' && (
                <button className="ghost tiny" style={{ marginTop: 6 }}
                        onClick={() => setNewNumber({
                          label: '', phone: '', save: can('phonebook.add') ? 'caller' : 'no', name: '',
                        })}>
                  + Add a number
                </button>
              )}
              {numberNote && <div className="bookerr">{numberNote}</div>}
              {/* WHERE IT GOES IN THE PHONE BOOK (25 Oct 2026). */}
              {newNumber && can('phonebook.add') && (
                <BookChoice save={newNumber.save} name={newNumber.name} label={newNumber.label}
                            caller={call.anonymous ? '' : (call.caller_name ?? '')}
                            onChange={(save, name) => setNewNumber({ ...newNumber, save, name })} />
              )}
            </div>
          </div>

          <div className="box">
            <h4>
              Location
              {call.relocated_count > 0 && (
                <span className="pill wait">Moved ×{call.relocated_count}</span>
              )}
            </h4>
            <div className="bd">
              <dl className="kv">
                <dt>Now at</dt><dd>{call.locations.at(-1)?.label ?? '—'}</dd>
                {/* THE LOCATION DETAILS (071) when there is an address as well;
                    with none the line above already is them. */}
                {call.locations.at(-1)?.details
                  && call.locations.at(-1)!.details !== call.locations.at(-1)!.label && (
                  <>
                    <dt>Details</dt>
                    <dd>{call.locations.at(-1)!.details}</dd>
                  </>
                )}
                {call.locations.at(-1)?.cross_street_a && call.locations.at(-1)?.cross_street_b && (
                  <>
                    <dt>Between</dt>
                    <dd>{call.locations.at(-1)!.cross_street_a} and {call.locations.at(-1)!.cross_street_b}</dd>
                  </>
                )}
                {/* THE NOTE ON THIS LOCATION (060), corrected in place. The
                    thread records the change like a cross-street correction. */}
                <dt>Directions</dt>
                <dd>
                  {noteEdit !== null ? (
                    <span className="addnumber">
                      <input
                        autoFocus
                        maxLength={400}
                        value={noteEdit}
                        aria-label="Directions for finding the place"
                        placeholder="Back entrance, ring twice"
                        onChange={(e) => setNoteEdit(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                            e.preventDefault()
                            act(async () => {
                              await api.incidentLocationNote(call.id, noteEdit.trim() || null)
                              setNoteEdit(null)
                            })
                          }
                          if (e.key === 'Escape') setNoteEdit(null)
                        }}
                      />
                      <button className="btn sm"
                              onClick={() => act(async () => {
                                await api.incidentLocationNote(call.id, noteEdit.trim() || null)
                                setNoteEdit(null)
                              })}>
                        Save
                      </button>
                      <button className="ghost tiny" onClick={() => setNoteEdit(null)}>Cancel</button>
                    </span>
                  ) : (
                    <>
                      {call.locations.at(-1)?.note ?? <span className="muted">—</span>}
                      {call.status !== 'closed' && (
                        <button className="ghost tiny" style={{ marginLeft: 8 }}
                                onClick={() => setNoteEdit(call.locations.at(-1)?.note ?? '')}>
                          {call.locations.at(-1)?.note ? 'Edit' : '+ Add'}
                        </button>
                      )}
                    </>
                  )}
                </dd>
                {call.broadcast_area && (
                  <>
                    <dt>Broadcast as</dt>
                    <dd>{call.broadcast_area} <em>— the address goes only to whoever takes it</em></dd>
                  </>
                )}
                <dt>Verified</dt>
                <dd>
                  {call.pinned
                    ? <>Pinned on the map{call.pinned.at ? ` at ${hhmm(call.pinned.at)}` : ''}{call.pinned.by ? ` by ${call.pinned.by}` : ''}</>
                    : call.place?.latitude != null
                      ? 'On the map, from the address'
                      : <em>Raw — no coordinates</em>}
                </dd>
                {/* EVERY EARLIER ADDRESS, NEWEST FIRST, with when it was
                    given. The customer (item 2, 22 Sep 2026): keep the
                    original, never overwrite, make the current one clear --
                    which is "Now at" above, and these are what it was. */}
                {call.locations.length > 1 && (
                  <>
                    <dt>Before that</dt>
                    <dd style={{ color: 'var(--ink-faint)' }}>
                      {call.locations.slice(0, -1).reverse().map((l) => (
                        <div key={l.sequence}>
                          {l.label}
                          {l.note && <span> · {l.note}</span>}
                          <span className="mono" style={{ fontSize: 11, marginLeft: 6 }}>
                            until {hhmm(call.locations[l.sequence + 1]?.moved_at ?? l.moved_at)}
                          </span>
                        </div>
                      ))}
                    </dd>
                  </>
                )}
              </dl>
            </div>
            {/* THE CALL MOVED. Behind the key the route enforces, so a role
                without it never sees a button that answers with a refusal.
                The old address stays on the trail above; the responders
                already going are asked whether they still are, which the
                Dispatch tab chases; the new address goes out with the next
                broadcast and the next "Resend full address". */}
            {can('calls.incident_record.location_history') && call.status !== 'closed' && (
              moving ? (
                <MoveForm
                  onCancel={() => setMoving(false)}
                  onMoved={async (body) => {
                    await act(async () => {
                      const r = await api.incidentRelocate(call.id, body)
                      setMoving(false)
                      // WHAT REACHED WHOM, beside the handover buttons on the
                      // Dispatch tab, where the other sends say it.
                      if (r.details?.sent) {
                        setHandNote(reached('The new address', r.details.to, r.details.by, r.details.radio_only))
                      } else if (r.details) {
                        setHandNote({ ok: false, text: r.details.detail ?? 'The new address was not sent.' })
                      }
                    })
                  }}
                />
              ) : (
                <div className="bd" style={{ borderTop: '1px solid var(--line-soft)' }}>
                  <button className="btn sm" onClick={() => setMoving(true)}>
                    The call moved — new address
                  </button>
                </div>
              )
            )}
          </div>
        </div>

        {/* EVERY CALLER (063), when there is more than one. */}
        <CallerRows call={call} />
        </div>
        <div className="wcol">

        {/* WHAT THE NATURE ASKS, AND WHAT IS STILL MISSING. Below the caller
            and the location because a ticket is read from the top and those two
            are what a responder needs first; above Responding because these are
            still being collected while the call runs.
            KEYED ON THE CALL, so the answers in the boxes cannot follow the
            dispatcher to the next one — the same reason the narrative draft is
            cleared on `call.id`. */}
        {/* THE CALL'S NOTES (081, his Q1 A), at the top of this column as
            they were under the nature on Take a call: more is added, each
            with who and when, never written over. A note for the responder
            says whom it reached and offers Resend to anybody going without it. */}
        {/* ITS OWN KEY, NEVER THE BARE CALL ID: `CallAnswers` below is a
            sibling keyed on `call.id`, and two siblings with one key are
            reconciled wrongly -- switching calls left the last call's notes
            box on screen, still bound to that call, above this one's (the
            customer's "mixed together across different calls"). */}
        <CallNotes key={`notes-${call.id}`} callId={call.id} notes={call.call_notes ?? []}
                   anonymous={call.anonymous}
                   onAdded={() => act(async () => {})}
                   going={going.map((r) => ({ member_id: r.member_id, who: r.who,
                                              reachable: r.reachable }))}
                   canSend={can('calls.active.actions.broadcast')} />

        {/* ITS NATURES, AND ANOTHER ONE PUT ON IT (076). */}
        <CallNatures call={call} natures={natures} onChanged={() => act(async () => {})} />

        <CallAnswers
          /* ON THE CALL ONLY: a nature put on or taken off changes what is
             asked, and the panel follows it without starting again. */
          key={call.id}
          call={call}
          /* An empty piece of work through `act`, which is how everything else
             on this screen re-reads: an answer may have raised the priority and
             may have flagged the call, and both of those are drawn by the queue
             and the ticket rather than by the panel that changed them. */
          onChanged={() => act(async () => {})}
        />
        </div>
        </div>

        </div>

        {/* ---- 2 · Dispatch: sending it out, and who is going --------- */}
        {/* WHO IS GOING AND WHAT THEY WERE SENT, BESIDE THE THREAD: a press
            and what it did are on screen together. */}
        <div className="wpanel" hidden={tab !== 'dispatch'}>
        <div className="wcols2">
        <div className="wcol">
        {/* As words to say, once somebody has actually taken it. The card app
            does not exist yet, so the dispatcher IS the delivery mechanism —
            the plan's "a radio-only responder gets it over the air from the
            dispatcher". */}
        {going.length > 0 && (
          <Handover place={place} going={going.map((r) => r.who)} />
        )}

        <div className="box" style={{ marginTop: going.length ? 15 : 0 }}>
          <h4>
            Responding
            <span className={`pill ${going.length ? 'go' : 'alarm'}`}>{going.length}</span>
          </h4>
          {listed.length === 0 ? (
            <div className="bd" style={{ color: 'var(--ink-faint)', fontSize: 13.5 }}>
              Nobody has taken this yet.
            </div>
          ) : (
            listed.map((r) => r.removed_at ? (
              /* TAKEN OFF, LOGGED BY MISTAKE (080): crossed out, never gone. */
              <div className="resp rmv-off" key={r.id}>
                <span className="cs">{r.who}</span>
                <div className="nm">
                  <s>{r.who}</s>
                  <small>
                    removed {hhmm(r.removed_at)} by {r.removed_by_label ?? 'a dispatcher'}
                    {' — '}{r.removed_reason ?? 'logged by mistake'}
                  </small>
                </div>
                <span className="pill rmv-pill">removed</span>
              </div>
            ) : (
              <Fragment key={r.id}>
              <div className={`resp ${r.released_at ? 'released' : ''}`}>
                <span className="cs">{r.who}</span>
                <div className="nm">
                  {r.who}
                  <small>
                    {r.role} · {r.accepted_at ? `accepted ${hhmm(r.accepted_at)}` : 'time not recorded'} · {r.accepted_via}
                    {r.relocation_ack === 'pending' && ' · has not confirmed the move'}
                    {r.released_at && ' · no longer going'}
                  </small>
                </div>
                {/* STILL GOING, OR NOT, after the call moved. The server has
                    kept these two answers since the relocation stage was
                    built; the row said "has not confirmed" with no way to
                    record the answer. Whoever radios it in, the dispatcher
                    presses it here. */}
                {r.relocation_ack === 'pending' && !r.released_at && (
                  <div className="ack">
                    <button className="btn sm" title="He is still going, to the new address"
                            onClick={() => act(() => api.incidentRelocationAck(call.id, r.id, true))}>
                      Still going
                    </button>
                    <button className="ghost tiny" title="He is not going any more"
                            onClick={() => act(() => api.incidentRelocationAck(call.id, r.id, false))}>
                      Released
                    </button>
                  </div>
                )}
                {/* HIS OWN ARRIVAL, BESIDE HIS NAME. The customer: "responder
                    one arrived at this time, responder two arrived on that
                    time" -- the one call-level button could record only the
                    first. The call's own On scene is the first arrival and is
                    kept by the server. */}
                <div className="arrival">
                  {r.on_scene_at ? (
                    <>
                      <span className="pill go">on scene {hhmm(r.on_scene_at)}</span>
                      <button className="ghost tiny" disabled={arriving !== null}
                              onClick={() => arrived(r.id, r.who, true)}>
                        undo
                      </button>
                    </>
                  ) : !r.released_at && (
                    <button className="btn sm" disabled={arriving !== null}
                            onClick={() => arrived(r.id, r.who, false)}>
                      {arriving === r.id ? 'Saving…' : 'Arrived on scene'}
                    </button>
                  )}
                  {/* LOGGED BY MISTAKE (080, his Q1 A): whoever may log who
                      accepted may take him off. Asked first, below. */}
                  {!r.released_at && can('calls.active.actions.log_acceptance') && (
                    <button className="btn sm rmv-btn" disabled={removing?.busy}
                            aria-expanded={removing?.id === r.id}
                            onClick={() => setRemoving(removing?.id === r.id ? null
                              : { id: r.id, reason: '', busy: false })}>
                      Remove
                    </button>
                  )}
                </div>
              </div>
              {removing?.id === r.id && (
                <div className="rmv-ask" role="group" aria-label={`Take ${r.who} off this call`}>
                  <b>Take {r.who} off this call?</b> He stays on the record, crossed
                  out — logged by mistake.
                  <input type="text" value={removing.reason} maxLength={400} autoFocus
                         placeholder="Why (optional) — e.g. pressed the wrong unit"
                         aria-label="Why he is taken off (optional)"
                         onChange={(e) => setRemoving({ ...removing, reason: e.target.value })}
                         onKeyDown={(e) => {
                           if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                             e.preventDefault(); removeResponder(r)
                           } else if (e.key === 'Escape') {
                             e.stopPropagation(); setRemoving(null)
                           }
                         }} />
                  <div className="rmv-btns">
                    <button className="btn sm rmv-yes" disabled={removing.busy}
                            onClick={() => removeResponder(r)}>
                      {removing.busy ? 'Removing…' : `Remove ${r.who}`}
                    </button>
                    <button className="ghost tiny" disabled={removing.busy}
                            onClick={() => setRemoving(null)}>
                      Keep him
                    </button>
                  </div>
                </div>
              )}
              </Fragment>
            ))
          )}
          {/* Drawn only for a role that may log one. The server refuses either
              way, and this refusal landed worse than most: the select is reset
              to its placeholder BEFORE the request goes out, so a 403 took the
              dispatcher's choice with it. He was left looking at "Log who
              accepted…" and a red line, with nothing to say whether the man who
              had just called in on the radio was on the record or not — mid
              call, which is the one moment there is no time to find out. The
              wrapper goes with it, or an empty bordered strip stays behind. */}
          {can('calls.active.actions.log_acceptance') && (
            <div className="bd" style={{ borderTop: '1px solid var(--line-soft)' }}>
              {/* TYPED, NOT SCROLLED (item 18, 22 Sep 2026): "typing any
                  associated name / member number / phone number ... should
                  immediately find the appropriate member." The search runs
                  on the server, which is the only place a number can be
                  matched without being handed to this browser. */}
              <MemberPicker
                roster={members}
                exclude={going.map((g) => g.member_id)}
                onPick={(id) => {
                  setHandNote(null)
                  const send = sendDetails
                  setSendDetails(sendStart)
                  act(async () => {
                    const r = await api.incidentAccept(call.id, {
                      member_id: id, role: going.length ? 'backup' : 'primary',
                      send_details: send,
                    })
                    // HE IS ON THE RECORD EITHER WAY. Whether the details
                    // reached his phone is said beside the buttons, because a
                    // text server having a bad minute is something the
                    // dispatcher can fix by reading them out on the radio.
                    if (r.details?.withheld) {
                      setHandNote({ ok: true, text: `${r.who} is logged as going. Nothing was sent to him.` })
                    } else if (r.details?.sent) {
                      setHandNote(reached('Full address & details', r.details.to ?? [r.who],
                                          r.details.by, r.details.radio_only))
                    } else if (r.details) {
                      setHandNote({
                        ok: false,
                        text: `${r.who} is logged as going, but the full address was not`
                          + ` sent: ${r.details.detail ?? 'unknown reason'}`,
                      })
                    }
                  })
                }}
              />
              {/* LOG HIM, SEND NOTHING (078, the customer: "log a member that
                  he said that he's going without it should send him any
                  message"). On, as it always was. */}
              <label className="sendtick">
                <input type="checkbox" checked={sendDetails}
                       onChange={(e) => setSendDetails(e.target.checked)} />
                <span>Send him the full address &amp; details
                  <small>{call.logged_afterwards
                    ? 'This call was logged afterwards, so this starts unticked: tick it to send.'
                    : 'Untick to log him as going and send nothing — he already has it, or was told on the radio.'}</small>
                </span>
              </label>
            </div>
          )}
          {/* THE HANDOVER MESSAGES. The full address, caller's name and every
              answer went to him automatically when he was logged; the resend is
              for a message that did not arrive, and the other two go only on a
              press. Gate codes need the key that shows them, and the
              callback number is its own button so it is never sent by
              accident with anything else. */}
          {going.length > 0 && can('calls.active.actions.broadcast') && (
            <div className="bd handsend" style={{ borderTop: '1px solid var(--line-soft)' }}>
              {can('calls.incident_record.entry_codes') && (
                <button className="btn sm" disabled={handing !== null}
                        onClick={() => sendToGoing('site')}>
                  {handing === 'site' ? 'Sending…' : 'Send gate codes & site notes'}
                </button>
              )}
              {!call.anonymous && (
                <button className="btn sm" disabled={handing !== null}
                        onClick={() => sendToGoing('callback')}>
                  {handing === 'callback' ? 'Sending…' : 'Send callback number'}
                </button>
              )}
              <button className="btn sm" disabled={handing !== null}
                      onClick={() => sendToGoing('details')}>
                {handing === 'details' ? 'Sending…' : 'Resend full address & details'}
              </button>
              {handNote && (
                <span className={`handnote ${handNote.ok ? 'ok' : 'warn'}`} role="status">
                  {handNote.text}
                </span>
              )}
            </div>
          )}
        </div>

        {sends.length > 0 && (
          <div className="box" style={{ marginTop: 15 }}>
            <h4>
              Broadcasts
              <span className="pill">{sends.length}</span>
            </h4>
            <SendRows sends={sends} />
            <div className="bd" style={{ borderTop: '1px solid var(--line-soft)' }}>
              <p className="footnote" style={{ margin: 0 }}>
                Delivered means the text reached the handset. Sent means only
                that the carrier accepted it — neither means anybody read it,
                and neither is an acceptance: that is still radio or card. A
                member with no channel switched on is not in these numbers at
                all — check the roster if a count looks short.
              </p>
            </div>
          </div>
        )}

        </div>
        <div className="wcol">
        <div className="box">
          <h4>The thread</h4>
          <div className="bd">
            <ThreadRows thread={call.thread} />
          </div>

          {/* The narrative: what happened, in words, as it happens. Drawn only
              for a role that holds the key the route enforces -- a composer
              that answers a typed sentence with a red line is worse than no
              composer at all, because the dispatcher then has to work out
              mid-call whether what he wrote is on the record or gone. A role
              without it keeps the thread above, which is the half it may
              read. */}
          {can('calls.incident_record.narrative') && (
            <div className="bd" style={{ borderTop: '1px solid var(--line-soft)' }}>
              <textarea
                className="orgpick"
                style={{
                  display: 'block', width: '100%', maxWidth: 'none',
                  resize: 'vertical', lineHeight: 1.5,
                }}
                rows={2}
                value={note}
                aria-label="Add to the narrative"
                placeholder="What is happening, in words. Enter adds it."
                /* The route takes 1–4000 characters, and FastAPI's refusal past
                   that is a Pydantic error ARRAY: `request` hands `detail`
                   straight to the banner, where an array of objects renders as
                   nothing anybody can read. Stopping at the ceiling means a long
                   narrative is never typed past the point where it can be lost
                   to a message that does not say what went wrong. */
                maxLength={4000}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => {
                  // ENTER ADDS IT, SHIFT+ENTER BREAKS THE LINE. This is written
                  // one-handed while the caller is still talking, so a composer
                  // that needs the mouse after every observation is one where
                  // the observations stop being written down -- which is the
                  // narrative not existing, the thing this box is for. The
                  // composition guard is not decoration: with an IME, Enter is
                  // how a candidate word is ACCEPTED, so without it a French or
                  // Hebrew dispatcher's first keystroke files half a word and
                  // clears the box.
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault()
                    addNote()
                  }
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                <button
                  className="btn primary"
                  /* Whitespace is not a note. The server refuses under one
                     character, and a 422 in the banner for pressing a button on
                     an empty box is an error where an answer was expected. */
                  disabled={!note.trim() || posting}
                  onClick={addNote}
                >
                  {posting ? 'Adding…' : 'Add to the thread'}
                </button>
                <span className="footnote" style={{ margin: 0 }}>
                  Shift+Enter for a new line. A note joins the record here and
                  is sent to nobody — reaching people is still Broadcast.
                </span>
              </div>
            </div>
          )}
        </div>
        </div>
        </div>

        </div>

        {/* ---- 3 · Close: how it ended -------------------------------- */}
        {/* HOW IT ENDED, beside what is already on the record -- Close asks
            only what nobody knows yet. */}
        <div className="wpanel" hidden={tab !== 'close'}>
        <div className="wcols2">
        <div className="wcol">
          {/* ON THE SAME KEY AS THE BUTTON. A role that may not close a call
              gets a sentence here rather than chips and a box that lead to
              no button -- a granted-looking screen with nothing behind it. */}
          {can('calls.active.actions.close') ? (
          <div className="box">
            <h4>Close — everything else is already on the record</h4>
            <div className="bd">
              <div className="chips" style={{ marginBottom: 12 }}>
                {resolutions.filter((r) => !r.retired_at).map((r) => (
                  <button
                    key={r.id}
                    className={`chip ${resolutionId === r.id ? 'on' : ''}`}
                    onClick={() => { setResolutionId(r.id); setResolutionLabel(r.name) }}
                  >
                    {r.name}
                  </button>
                ))}
              </div>
              <input
                className="orgpick"
                style={{ width: '100%', maxWidth: 'none' }}
                placeholder="Anything else worth saying"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
              />
              {call.has_open_equipment && (
                <p className="footnote">
                  Equipment is still out on this call. That does not hold it
                  open — the item stays open as its own thread.
                </p>
              )}
            </div>
          </div>
          ) : (
          <div className="box">
            <h4>Close</h4>
            <div className="bd" style={{ color: 'var(--ink-faint)', fontSize: 13.5 }}>
              Closing a call is not part of your role. Marking it covered is.
            </div>
          </div>
          )}
        </div>
        <div className="wcol">
          <div className="box">
            <h4>On the record already</h4>
            <div className="bd">
              <dl className="kv">
                {steps.map(([label, value]) => (
                  <ClosedFact key={label} label={label} value={value} />
                ))}
                <dt>Responders</dt>
                <dd>{call.responders.length
                  ? call.responders.map((r) => r.who + (r.removed_at ? ' (taken off)'
                      : r.released_at ? ' (no longer going)' : '')).join(', ')
                  : 'Nobody was logged as going'}</dd>
                <dt>Equipment out</dt>
                <dd>{call.has_open_equipment ? 'Yes — it stays open as its own thread' : 'None'}</dd>
              </dl>
            </div>
          </div>
        </div>
        </div>
        </div>
      </div>

      {/* THE BUTTON BAR FOLLOWS THE TAB. Each ends in the way to the next,
          so a call can be walked left to right; the last one is where it
          ends. The buttons themselves and their gates are exactly what they
          were -- only their room changed. */}
      {tab === 'caller' && (
        <div className="actions">
          <span className="sep" />
          <button className="btn primary" onClick={() => setTab('dispatch')}>
            Next: Dispatch →
          </button>
        </div>
      )}

      {tab === 'dispatch' && can('calls.active.actions.broadcast') && (
        <div className="sendto">
          <span className="lbl">{call.broadcast_at ? 'Re-broadcast to' : 'Broadcast to'}</span>
          <AudiencePicker audiences={audiences} value={audience}
                          onChange={setAudience} disabled={sending} />
        </div>
      )}
      {tab === 'dispatch' && (
      <div className="actions">
        {/* Drawn only for a role that may use it. The server refuses either
            way — this is so a dispatcher does not press Broadcast mid-call and
            get a red line back, having to work out whether he pressed the
            wrong thing or needs to ring somebody. */}
        {can('calls.active.actions.broadcast') && (
          <button
            className="btn go"
            disabled={sending || aimedAtNobody}
            onClick={async () => {
              setSending(true)
              try {
                await act(() => api.incidentBroadcast(call.id,
                  call.broadcast_at ? 'rebroadcast' : 'initial', audience))
              } finally {
                setSending(false)
              }
            }}
          >
            {sending ? 'Sending…' : (call.broadcast_at ? 'Re-broadcast' : 'Broadcast')}
          </button>
        )}
        <span className="sep" />
        <button className="btn primary" onClick={() => setTab('close')}>
          Next: Close →
        </button>
      </div>
      )}

      {tab === 'close' && (
      <div className="actions">
        {/* NO "MARK ON SCENE" HERE ANY MORE. Arrival is per responder, on his
            row under Responding; the call's On scene is the first of them. */}
        <button
          className="btn"
          disabled={!!call.covered_at}
          onClick={() => act(() => api.incidentTimeline(call.id, 'covered'))}
        >
          Covered
        </button>
        <span className="sep" />
        {closing ? (
          <>
            <button className="btn" onClick={() => setClosing(false)}>Not yet</button>
            <button
              className="btn primary"
              disabled={!resolutionLabel}
              onClick={() => act(async () => {
                await api.incidentClose(call.id, {
                  resolution_id: resolutionId || null,
                  resolution_label: resolutionLabel,
                  outcome_note: outcome || null,
                })
                onClosed()
              })}
            >
              Confirm and close
            </button>
          </>
        ) : can('calls.active.actions.close') ? (
          <button className="btn primary" onClick={() => setClosing(true)}>
            Close call
          </button>
        ) : null}
      </div>
      )}
      </div>
      </div>
    </>
  )
}

/** Whether an element is at least `min` pixels wide, measured as it changes
 *  -- for a layout decided by the room a box has rather than by the window. */
function useRoomy(min: number) {
  const ref = useRef<HTMLDivElement>(null)
  const [roomy, setRoomy] = useState(true)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => setRoomy(entries[0].contentRect.width >= min))
    ro.observe(el)
    return () => ro.disconnect()
  }, [min])
  return [ref, roomy] as const
}

function ClosedFact({ label, value }: { label: string; value: string | null }) {
  return <><dt>{label}</dt><dd>{value ?? '—'}</dd></>
}

/** THE CALL AT A GLANCE (style B): on every tab of the open call, so the
 *  facts a responder needs first are never one tab away. It READS; changing
 *  anything is the Caller tab's, and "change" goes there. The map is the
 *  call's own, with its lookup, moved here from the Caller tab. */
function CallSummary({ call, place, going, onLocated, onCallerTab }: {
  call: IncidentDetail
  place: IncidentDetail['place']
  going: IncidentDetail['responders']
  onLocated: (p: IncidentDetail['place']) => void
  /** Null on the Caller tab, where the notes already are. */
  onCallerTab: (() => void) | null
}) {
  const now = call.locations.at(-1)
  // EVERY NOTE, DANGER AND CAUTION FIRST, each with who wrote it and when --
  // the site-notes rule: a note is weighed by its age and its author, and the
  // server's newest-first order could hide an older "dog in the yard" under
  // "and 2 more".
  const RANK = { danger: 0, caution: 1, info: 2 } as const
  const notes = [...(place?.notes ?? [])].sort((a, b) => RANK[a.severity] - RANK[b.severity])
  const codes = !!(place?.entry_codes || '').trim()
  const warn = codes || notes.some((n) => n.severity !== 'info')
  return (
    <aside className="wsum" aria-label="This call at a glance">
      {/* BEFORE ANYONE GOES, FIRST AND IN FULL: the dog and the gate code are
          what a responder needs before anything else. The code itself stays
          on the Caller tab, behind the key that shows it. */}
      <div className={`wsum-before ${warn ? 'has' : notes.length ? 'info' : ''}`}>
        <div className="wsum-l">Before anyone goes</div>
        {!place ? <div className="none">No address on record yet.</div>
          : !notes.length && !codes ? <div className="none">Nothing recorded about this address yet.</div>
            : <>
                {notes.map((n) => (
                  <div key={n.id} className={`sn ${n.severity}`}>
                    {n.body}
                    <small>{[n.author_label, n.written_at && new Date(n.written_at).toLocaleDateString()]
                      .filter(Boolean).join(' · ')}</small>
                  </div>
                ))}
                {codes && <div className="sn"><b>Entry codes on file</b></div>}
              </>}
        {onCallerTab && (
          <button type="button" className="linkish" onClick={onCallerTab}>Read and edit on the Caller tab</button>
        )}
      </div>

      <div className="wsum-l">Caller</div>
      <div className="wsum-v">
        {call.anonymous && <b className="tk-anon">Anonymous · </b>}
        {[call.caller_name, call.caller_phone].filter(Boolean).join(' · ') || 'Unknown caller'}
        {call.relationship_label && <div className="tk-sub">calling for {call.relationship_label.toLowerCase()}</div>}
        {call.caller_on_scene === false && <div className="tk-wait">not staying on scene</div>}
      </div>

      <div className="wsum-l">Where</div>
      <div className="wsum-v">
        {now?.label ?? '—'}
        {now?.details && now.details !== now.label && <div className="tk-sub">{now.details}</div>}
        {now?.cross_street_a && now?.cross_street_b && (
          <div className="tk-sub">between {now.cross_street_a} and {now.cross_street_b}</div>
        )}
        {now?.note && <div className="tk-sub">{now.note}</div>}
      </div>
      <PlaceMap place={place} onLocated={onLocated} pinned={call.pinned} height={160} />

      <div className="wsum-l">Answers</div>
      <div className="wsum-v">
        {call.answers.length
          ? call.answers.map((a) => (
              <div key={a.id} className={a.is_alert ? 'tk-red' : ''}>
                {a.prompt} <b>{a.answer}</b>
              </div>
            ))
          : <span className="none">Nothing answered yet</span>}
      </div>

      <div className="wsum-l">Going</div>
      <div className="wsum-v">
        {going.length
          ? going.map((r) => (
              <div key={r.id}>
                <b>{r.who}</b> · {r.role}{r.on_scene_at ? ` · on scene ${hhmm(r.on_scene_at)}` : ''}
              </div>
            ))
          : <span className="none">Nobody yet</span>}
      </div>
    </aside>
  )
}
